import { CaptureUpdateAction, Excalidraw, Footer, MainMenu, ROUNDNESS, mutateElement, newElementWith, restoreElements } from '@excalidraw/excalidraw';
import type { ExcalidrawElement, OrderedExcalidrawElement } from '@excalidraw/excalidraw/element/types';
import type { AppState, BinaryFiles, ExcalidrawImperativeAPI, SocketId } from '@excalidraw/excalidraw/types';
import { isDevOrigin } from '@redline/shared/env';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type Board, type BoardsApi } from './boardsApi';
import type { ConnState } from './collab/BoardSocket';
import { ExcalidrawCollab, type PresenceUser } from './collab/ExcalidrawCollab';
import { detectPlatform, dispatchNativeKey, isTypingTarget, withKey } from '../../shared/src/keymap';
import { FONT, arrowElement, centreIn, labelledShape, polygonElement, starPoints, trianglePoints, type El } from './elements';
import { download, exportScope, frames, safeFilename, sceneHash, snapshot, thumbnailBlob, type ExportFormat, type ExportScope } from './exporting';
import { openShare, uploadDriveSnapshot } from './drive';
import { navigate } from './router';
import { buildSeed } from './seed';
import { STICKY_COLORS, STICKY_ORDER, stickyElements, type StickyColor } from './sticky';
import { StickyFitter, refitBrandText } from './stickyFit';
import { isTemplateId } from './templates';
import { MiroKeysExcalidraw, type PlaceKind } from './keyboard';
import { Minimap } from './Minimap';
import { CameraLayer, cameraClock, viewKey } from './camera';
import { excalidrawClientColor, initials, toast } from './ui';
import { EstimatePanel, estimateScopeChoices, type SelectedFixture, type SelectedFrame } from '../../shared/src/components/Estimate';
import { useWorkbookLink } from '../../shared/src/components/Workbook';
import { FIXTURE_PRESETS, countAlong, fixtureLabel, itemFromPreset, money, presetUom, type AlongRun, type FixtureData } from '../../shared/src/estimate';
import { nextTypeCode } from '../../shared/src/takeoff';
import { useBoardEstimate, type BoardEstimate } from '../../shared/src/hooks/useBoardEstimate';
import type { TakeoffTarget } from '../../shared/src/hooks/useTakeoff';
import { FIXTURE_H, FIXTURE_W, SYMBOL_KIND, estimateSignature, symbolBox, fixtureElements, fixtureScaleAt, isRunLike, kindOfElement, fixtureOf, frameAtPoint, frameName, framesOf, inkOn, pxLength, type FixtureTarget, type SceneEl } from './estimate';
import type { SelectedImage, SelectedMeasure } from '../../shared/src/components/EstimateMeasure';
import {
  SCALE_PRESETS,
  convertLength,
  formatArea,
  dimensionOf,
  dimensionText,
  formatLength,
  isAreaShape,
  isLoop,
  measureOf,
  measureOnImage,
  scaleFromCalibration,
  scaleFromPreset,
  scaleOf,
  sceneToNatural,
  sourceOf,
  type GeomEl,
  type ImageScale,
  type MeasureDisplay,
} from '../../shared/src/measure';
import { MeasureOverlay, dropPdf, imageName, naturalSize, type CalibrationState } from './measure';
import { MirrorOverlay, readMirrorKeep, writeMirrorKeep, type MirrorState } from './MirrorTool';
import { ZONE_COLOURS, ZoneOverlay, type ZoneDraft } from './ZoneTool';
import { fixturesByZone, nextZoneName, zoneOf, zonesOf } from '../../shared/src/zones';
import { axisFrom, mirrorSelection, verticalAxis, type MEl } from './mirror';
import { MeasureToolbar, type MeasureMode, type SelectedFixtureInfo, type SelectedLineInfo } from './MeasureToolbar';
import { DEFAULT_DBLCLICK, dblClickEditsPoints, distanceToPolyline, nextDisplay, removeLastVertex, type DblClickSettings } from '../../shared/src/measureTools';
import { scenePoints } from '../../shared/src/measure';

interface Props {
  boardId: string;
  boards: BoardsApi;
  user: { id: string; name: string; email: string };
}

type CustomTool = 'sticky' | 'triangle' | 'star';
type Theme = 'light' | 'dark';
const THEME_KEY = 'xb.theme';
const estimateKey = (boardId: string) => `xb.estimate.${boardId}`;
const DBL_KEY = 'xb.dblclick';

function readDbl(): DblClickSettings {
  try {
    const v = JSON.parse(localStorage.getItem(DBL_KEY) ?? 'null') as Partial<DblClickSettings> | null;
    return { line: v?.line ?? DEFAULT_DBLCLICK.line, arrow: v?.arrow ?? DEFAULT_DBLCLICK.arrow };
  } catch {
    return DEFAULT_DBLCLICK;
  }
}

const canvasHost = () => document.querySelector('.xb-canvas .excalidraw') ?? document.body;
/** Excalidraw's own "edit line points" (⌘/Ctrl + Enter on a selected line). */
function enterPointEditor() {
  dispatchNativeKey(canvasHost(), { key: 'Enter', code: 'Enter', ...(detectPlatform() === 'mac' ? { metaKey: true } : { ctrlKey: true }) });
}

/** Canvas theme: light unless this user switched to dark (Miro imports are drawn for a white canvas). */
function readTheme(): Theme {
  try {
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

const UOM_MEASURE: Record<string, FixtureTarget['measure']> = { LF: 'linear', M: 'linear', SF: 'area', M2: 'area' };

function targetFor(e: BoardEstimate, t: TakeoffTarget | null): FixtureTarget | null {
  if (!t) return null;
  if (t.kind === 'item') {
    const i = e.itemById.get(t.id);
    return i ? { kind: 'item', id: i.id, code: i.code, color: i.color, symbol: i.symbol, uom: i.uom, measure: UOM_MEASURE[i.uom] ?? 'count' } : null;
  }
  const a = e.assemblyById.get(t.id);
  if (!a) return null;
  const code = a.code || a.name.split(/\s+/).slice(0, 3).map((w) => w[0]).join('').toUpperCase() || 'HOST';
  return { kind: 'assembly', id: a.id, code, color: a.color, symbol: a.symbol, uom: a.measure === 'linear' ? 'LF' : a.measure === 'area' ? 'SF' : 'EACH', measure: a.measure };
}

/** Fixture labels ("LD02", "LD02 ×4") as an overlay, only once a symbol is big enough on screen to read. */
const LABEL_MIN_PX = 22;
function FixtureLabels({ xapi, tick, sceneKey, estimate }: { xapi: ExcalidrawImperativeAPI; tick: number; sceneKey: string; estimate: BoardEstimate }) {
  void tick; // re-render on every camera / scene change
  // Scene-space labels only change with the scene or the catalog; a camera frame just projects them.
  const placed = useMemo(() => {
    const out: { id: string; cx: number; bottom: number; w: number; text: string; color: string }[] = [];
    for (const e of xapi.getSceneElements() as unknown as SceneEl[]) {
      if (e.isDeleted || isRunLike(e)) continue;
      const f = fixtureOf(e);
      if (f?.role !== 'fixture') continue;
      const t = targetFor(estimate, targetOfFixture(f));
      if (!t) continue;
      const box = symbolBox(e);
      out.push({ id: e.id, cx: box.cx, bottom: box.bottom, w: box.w, text: fixtureLabel(t.code, f, t.uom), color: t.color });
    }
    return out;
  }, [xapi, sceneKey, estimate.itemById, estimate.assemblyById]); // eslint-disable-line react-hooks/exhaustive-deps
  const st = xapi.getAppState();
  const z = st.zoom.value;
  const labels: { id: string; x: number; y: number; text: string; color: string }[] = [];
  for (const l of placed) {
    if (l.w * z < LABEL_MIN_PX) continue;
    const x = (l.cx + st.scrollX) * z;
    const y = (l.bottom + st.scrollY) * z;
    if (x < -50 || y < -50 || x > st.width + 50 || y > st.height + 50) continue;
    labels.push({ id: l.id, x, y, text: l.text, color: l.color });
  }
  if (!labels.length) return null;
  return (
    <div className="xb-measure" data-testid="fixture-labels">
      {labels.map((l) => (
        <span key={l.id} className="xb-fixture-label" style={{ left: l.x, top: l.y, borderColor: l.color }} data-testid={`fixture-label-${l.id}`}>
          {l.text}
        </span>
      ))}
    </div>
  );
}

/** Zone names + fixture counts as an overlay at each zone's top edge (always on: zones are big). */
function ZoneLabels({ xapi, tick, zones }: { xapi: ExcalidrawImperativeAPI; tick: number; zones: { id: string; name: string; count: number; hidden?: boolean; pts: [number, number][] }[] }) {
  void tick;
  // `zones` is rebuilt per scene change (BoardPage's zoneList), so this is too — not per camera frame.
  const anchors = useMemo(() => {
    const byId = new Map(xapi.getSceneElements().map((e) => [e.id, e]));
    // Hidden zones ("set and forget") have no label; they still count.
    return zones
      .filter((d) => !d.hidden)
      .map((d) => {
        const xs = d.pts.map((p) => p[0]);
        return { id: d.id, cx: (Math.min(...xs) + Math.max(...xs)) / 2, top: Math.min(...d.pts.map((p) => p[1])), text: `${d.name} · ${d.count}`, colour: (byId.get(d.id)?.strokeColor as string) ?? '#1971c2' };
      });
  }, [xapi, zones]);
  const st = xapi.getAppState();
  const z = st.zoom.value;
  const out: { id: string; x: number; y: number; text: string; colour: string }[] = [];
  for (const a of anchors) {
    const x = (a.cx + st.scrollX) * z;
    const y = (a.top + st.scrollY) * z;
    if (x < -200 || y < -60 || x > st.width + 200 || y > st.height + 60) continue;
    out.push({ id: a.id, x, y, text: a.text, colour: a.colour });
  }
  if (!out.length) return null;
  return (
    <div className="xb-measure" data-testid="zone-labels">
      {out.map((l) => (
        <span key={l.id} className="xb-zone-label" style={{ left: l.x, top: l.y, borderColor: l.colour, color: l.colour }} data-testid={`zone-label-${l.id}`}>
          {l.text}
        </span>
      ))}
    </div>
  );
}

function targetOfFixture(f: FixtureData): TakeoffTarget | null {
  return f.item_id ? { kind: 'item', id: f.item_id } : f.assembly_id ? { kind: 'assembly', id: f.assembly_id } : null;
}
const THUMB_INTERVAL_MS = 2 * 60_000;
/**
 * Text style for new elements: "clean" (the brand font, Karla — served behind Excalidraw's Nunito
 * family, see web/shared/vite.fonts.ts — and no rough edges) by default, or the hand-drawn look.
 * A new key: the old `xb.cleanStyle` defaulted to hand-drawn and was written on every visit.
 */
const STYLE_KEY = 'xb.textStyle';

function readClean(): boolean {
  try {
    return localStorage.getItem(STYLE_KEY) !== 'hand';
  } catch {
    return true;
  }
}

function syncUrl(boardId: string, token: string): string {
  const base = (import.meta.env.VITE_SYNC_BASE as string | undefined) ?? `${location.origin.replace(/^http/, 'ws')}/sync`;
  return `${base}/excalidraw/${encodeURIComponent(boardId)}?token=${encodeURIComponent(token)}`;
}

const randomId = () => {
  const a = new Uint8Array(12);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 20);
};

export function BoardPage({ boardId, boards, user }: Props) {
  // Dev / e2e only: page renders, so a test can assert pan / zoom doesn't re-render the page.
  if (isDevOrigin()) (window as unknown as { __xbRenders?: number }).__xbRenders = ((window as unknown as { __xbRenders?: number }).__xbRenders ?? 0) + 1;
  const [theme, setTheme] = useState<Theme>(readTheme);
  const themeRef = useRef(theme);
  themeRef.current = theme;
  const [board, setBoard] = useState<Board | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [xapi, setXapi] = useState<ExcalidrawImperativeAPI | null>(null);
  const xapiRef = useRef<ExcalidrawImperativeAPI | null>(null);
  xapiRef.current = xapi;
  const [conn, setConn] = useState<ConnState>('connecting');
  const [ready, setReady] = useState(false);
  const [users, setUsers] = useState<PresenceUser[]>([]);
  const [clean, setClean] = useState(readClean);
  const [stickyColor, setStickyColor] = useState<StickyColor>('yellow');
  const [activeTool, setActiveTool] = useState<string>('selection');
  const [following, setFollowing] = useState<string | null>(null);
  const [minimap, setMinimap] = useState(false);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);
  const collabRef = useRef<ExcalidrawCollab | null>(null);
  const fitterRef = useRef<StickyFitter | null>(null);
  const fitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const thumbHash = useRef<number | null>(null);
  /** Latest scene as seen by onChange — Excalidraw clears its scene on unmount before our cleanup runs. */
  const lastScene = useRef<{ elements: readonly OrderedExcalidrawElement[]; appState: AppState; files: BinaryFiles } | null>(null);
  const stickyColorRef = useRef(stickyColor);
  stickyColorRef.current = stickyColor;
  const cleanRef = useRef(clean);
  cleanRef.current = clean;
  const readOnly = board?.role === 'viewer';

  // ---- design estimate -----------------------------------------------------------------
  const estimate = useBoardEstimate({ boardId, api, enabled: !!board });
  const display: MeasureDisplay = estimate.state?.pricing?.measure_display ?? 'ftin';
  const estimateRef = useRef(estimate);
  estimateRef.current = estimate;
  const [estimateOpen, setEstimateOpen] = useState(() => readFlag(estimateKey(boardId)));
  const [armed, setArmed] = useState<TakeoffTarget | null>(null);
  const armedRef = useRef(armed);
  armedRef.current = armed;
  const [selection, setSelection] = useState<{ fixture: SelectedFixture | null; frame: SelectedFrame | null; line: { elementId: string; px: number; frameId: string | null; frameName: string } | null; image: string | null; zone?: string | null; key: string }>({ fixture: null, frame: null, line: null, image: null, zone: null, key: '' });
  // ---- image scale + measuring (measure.tsx) ----
  /** Ticks (once per frame) on every onChange; only the overlays in the CameraLayer follow it (camera.tsx). */
  const camera = useMemo(() => cameraClock(), []);
  const tickRaf = useRef<number | null>(null);
  /**
   * Changes with the scene and the app state the page shows (viewKey) — not on pan / zoom. The page
   * re-renders on it and derived data (zone membership, measure cards, labels' scene positions) keys
   * on it: recomputing fixturesByZone per camera frame made pinch-zoom choppy on a dense board.
   */
  const [sceneKey, setSceneKey] = useState('');
  const sceneKeyRef = useRef('');
  const [calibration, setCalibration] = useState<CalibrationState | null>(null);
  /** Dimension tool (⇧D / toolbar Dim): clicks so far. */
  const [dimension, setDimension] = useState<{ pts: [number, number][] } | null>(null);
  /** Mirror tool (⇧M / toolbar Mirror): the selection being mirrored and the axis clicks so far. */
  const [mirror, setMirror] = useState<MirrorState | null>(null);
  const mirrorRef = useRef<MirrorState | null>(null);
  mirrorRef.current = mirror;
  const [mirrorKeep, setMirrorKeep] = useState(readMirrorKeep);
  /** Zone tool (Z / toolbar Zone): the outline being drawn, then its name. */
  const [zoneDraft, setZoneDraft] = useState<ZoneDraft | null>(null);
  const zoneRef = useRef<ZoneDraft | null>(null);
  zoneRef.current = zoneDraft;
  /** What to start once the open zone is named (a tool asked for while drawing it). */
  const zonePending = useRef<(() => void) | null>(null);
  const clearZone = useCallback(() => {
    zoneRef.current = null;
    zonePending.current = null;
    setZoneDraft(null);
  }, []);
  const setZone = useCallback((d: ZoneDraft) => {
    zoneRef.current = d;
    setZoneDraft(d);
  }, []);
  /**
   * A tool, placement or key asked for while the Zone tool is on. Nothing drawn yet: the zone tool just
   * closes. Drawing with 3+ points: the shape closes and asks for its name first, then `run` starts. Fewer:
   * the zone is dropped and `run` starts. Naming: paused (the banner says how to finish). Placements
   * (quick add) never run while a zone is open.
   */
  const zoneInterrupt = useCallback(
    (run: () => void, kind: 'tool' | 'place' = 'tool'): boolean => {
      const d = zoneRef.current;
      if (!d || (!d.pts.length && !d.naming)) {
        if (d) clearZone();
        run();
        return true;
      }
      const nudge = { ...d, nudge: (d.nudge ?? 0) + 1 };
      if (kind === 'place' || d.naming) {
        if (d.naming && kind === 'tool') zonePending.current = run;
        setZone(nudge);
        return false;
      }
      if (d.pts.length >= 3) {
        zonePending.current = run;
        setZone({ ...d, dragging: false, naming: true });
        return false;
      }
      clearZone();
      run();
      return true;
    },
    [clearZone, setZone],
  );
  const zoneInterruptRef = useRef(zoneInterrupt);
  zoneInterruptRef.current = zoneInterrupt;
  /** Drawing image shown in the panel's image card (selected, or its 📏 badge clicked while locked). */
  const [focusImage, setFocusImage] = useState<string | null>(null);
  /** W / A: the Excalidraw line tool measuring a run / an area, optionally as a fixture of `target`. */
  const measureRef = useRef<{ kind: 'linear' | 'area'; target: TakeoffTarget | null } | null>(null);
  const roundnessRef = useRef<AppState['currentItemRoundness'] | null>(null);
  const [measuring, setMeasuring] = useState<{ kind: 'linear' | 'area'; target: TakeoffTarget | null } | null>(null);
  /** The line being drawn with W / A (live length label), finalised when Excalidraw lets go of it. */
  const draftRef = useRef<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [dbl, setDbl] = useState<DblClickSettings>(readDbl);
  const dblRef = useRef(dbl);
  dblRef.current = dbl;
  /** null until the first scene: an empty board's signature is '' too. */
  const estimateSig = useRef<string | null>(null);
  const quickAddRef = useRef<{ focus(): void } | null>(null);

  // ---- board record ------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    boards
      .get(boardId)
      .then((b) => !cancelled && setBoard(b))
      .catch((e: Error & { status?: number }) => !cancelled && setLoadError(e.status === 404 ? 'This board does not exist or you do not have access to it.' : e.message));
    return () => {
      cancelled = true;
    };
  }, [boardId, boards]);

  useEffect(() => {
    if (board) document.title = `${board.title} · Excalidraw Board`;
    return () => {
      document.title = 'Excalidraw Board';
    };
  }, [board?.title]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- thumbnails ------------------------------------------------------------------
  const uploadThumbnail = useCallback(
    async (opts: { keepalive?: boolean; snap?: ReturnType<typeof snapshot> } = {}) => {
      if (!xapi || readOnly) return;
      const snap = opts.snap ?? snapshot(xapi);
      if (!snap.elements.length) return;
      const h = sceneHash(snap);
      if (h === thumbHash.current) return;
      thumbHash.current = h;
      try {
        const blob = await thumbnailBlob(snap);
        if (blob) await boards.uploadThumbnail(boardId, blob, { keepalive: opts.keepalive });
        // Full-size copy for the board's Drive PDF (not on page hide: too big for keepalive).
        if (!opts.keepalive) void uploadDriveSnapshot(boardId, snap);
      } catch {
        thumbHash.current = null;
      }
    },
    [xapi, readOnly, boards, boardId],
  );

  // ---- collaboration -----------------------------------------------------------------
  useEffect(() => {
    if (!xapi || !board) return;
    const fitter = new StickyFitter(xapi);
    fitterRef.current = fitter;
    const template = isTemplateId(board.template) ? board.template : 'blank';
    const collab = new ExcalidrawCollab(xapi, {
      boardId,
      user,
      readOnly: board.role === 'viewer',
      socketUrl: (t) => syncUrl(boardId, t),
      getToken: () => api.tokens.access,
      refreshToken: () => api.refreshAccessToken(),
      uploadAsset: (file, name) => boards.uploadAsset(boardId, file, name),
      buildSeed: template === 'blank' ? undefined : () => buildSeed(template),
      onConnState: setConn,
      onUsers: setUsers,
      onRemoteApplied: (els) => fitter.noteRemote(els),
      onError: (m) => toast(m, 'error'),
      onInit: ({ first, seeded, elementCount }) => {
        setReady(true);
        // Once per element: re-wrap text moved from Nunito to Karla (the brand font) so it fits.
        if (first && board.role !== 'viewer') void refitBrandText(xapi).catch(() => undefined);
        if (first && elementCount > 0) {
          xapi.scrollToContent(xapi.getSceneElements(), { fitToContent: true, animate: false });
          // Fitting zooms in a lot on tiny boards; keep it readable.
          const z = xapi.getAppState().zoom.value;
          if (z > 1) xapi.updateScene({ appState: { zoom: { value: 1 as AppState['zoom']['value'] } } });
          if (seeded) xapi.scrollToContent(xapi.getSceneElements(), { fitToContent: true, animate: false });
        }
      },
    });
    collabRef.current = collab;
    collab.start();
    if (isDevOrigin()) (window as unknown as { __xb?: unknown }).__xb = { xapi, collab, fitter, board };

    const timer = setInterval(() => void uploadThumbnail(), THUMB_INTERVAL_MS);
    const onHide = () => {
      collab.flush();
      void uploadThumbnail({ keepalive: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      clearInterval(timer);
      window.removeEventListener('pagehide', onHide);
      const last = lastScene.current;
      const snap = last ? { elements: last.elements.filter((e) => !e.isDeleted), appState: last.appState, files: last.files } : snapshot(xapi);
      collab.stop();
      collabRef.current = null;
      void uploadThumbnail({ snap });
    };
  }, [xapi, board?.id, board?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- clean style ---------------------------------------------------------------------
  useEffect(() => {
    if (!xapi) return;
    xapi.updateScene({
      appState: {
        currentItemRoughness: clean ? 0 : 1,
        currentItemFontFamily: (clean ? FONT.Brand : FONT.Excalifont) as AppState['currentItemFontFamily'],
      },
      captureUpdate: CaptureUpdateAction.NEVER,
    });
    try {
      localStorage.setItem(STYLE_KEY, clean ? 'clean' : 'hand');
    } catch {
      /* ignore */
    }
  }, [xapi, clean]);

  // ---- placing custom shapes -------------------------------------------------------------
  const frameAt = useCallback(
    (x: number, y: number): string | null => {
      if (!xapi) return null;
      const fs = frames(xapi.getSceneElements()).filter((f) => x >= f.x && x <= f.x + f.width && y >= f.y && y <= f.y + f.height);
      return fs.length ? fs[fs.length - 1].id : null;
    },
    [xapi],
  );

  const addElements = useCallback(
    (els: El[], select: string) => {
      if (!xapi) return;
      const restored = restoreElements(els as never, null, { refreshDimensions: true, repairBindings: true });
      xapi.updateScene({
        elements: [...xapi.getSceneElementsIncludingDeleted(), ...restored],
        appState: { selectedElementIds: { [select]: true } as AppState['selectedElementIds'] },
        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
      });
    },
    [xapi],
  );

  const startEditingSelected = useCallback(() => {
    // Excalidraw opens the bound-text editor of a selected container on Enter.
    requestAnimationFrame(() => {
      const host = document.querySelector('.xb-canvas .excalidraw');
      host?.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: true }));
    });
  }, []);

  const placeCustom = useCallback(
    (tool: CustomTool, x: number, y: number) => {
      if (!xapi) return;
      const frameId = frameAt(x, y);
      const id = randomId();
      if (tool === 'sticky') {
        const els = stickyElements({ id, cx: x, cy: y, color: stickyColorRef.current, text: '', frameId, fontFamily: cleanRef.current ? FONT.Brand : FONT.Excalifont });
        // Random nonce / seed for user-created elements.
        addElements(els.map((e) => ({ ...e, versionNonce: Math.floor(Math.random() * 2 ** 31), seed: Math.floor(Math.random() * 2 ** 31) })), id);
        xapi.setActiveTool({ type: 'selection' });
        startEditingSelected();
        return;
      }
      const st = xapi.getAppState();
      const pts = tool === 'triangle' ? trianglePoints(x, y, 160) : starPoints(x, y, 90);
      const el = polygonElement(id, {
        points: pts,
        frameId,
        strokeColor: st.currentItemStrokeColor,
        backgroundColor: st.currentItemBackgroundColor,
        fillStyle: st.currentItemFillStyle,
        strokeWidth: st.currentItemStrokeWidth,
        roughness: st.currentItemRoughness,
        opacity: st.currentItemOpacity,
        versionNonce: Math.floor(Math.random() * 2 ** 31),
        seed: Math.floor(Math.random() * 2 ** 31),
      });
      addElements([el], id);
      xapi.setActiveTool({ type: 'selection' });
    },
    [xapi, frameAt, addElements, startEditingSelected],
  );

  /** Keyboard-created card (D) and comment pin (C), Miro style. */
  const placeKind = useCallback(
    (kind: PlaceKind, x: number, y: number) => {
      if (!xapi) return;
      const frameId = frameAt(x, y);
      const id = randomId();
      const fontFamily = cleanRef.current ? FONT.Brand : FONT.Excalifont;
      const nonce = () => Math.floor(Math.random() * 2 ** 31);
      const els =
        kind === 'card'
          ? labelledShape('rectangle', id, {
              x: x - 150,
              y: y - 80,
              width: 300,
              height: 160,
              backgroundColor: '#ffffff',
              strokeColor: '#4262ff',
              strokeWidth: 2,
              fillStyle: 'solid',
              roughness: 0,
              roundness: { type: 3 },
              frameId,
              customData: { boardKind: 'card' },
              label: '',
              fontSize: 20,
              fontFamily,
            })
          : stickyElements({ id, cx: x, cy: y, color: 'yellow', size: 120, text: '', frameId, fontFamily }).map((e) =>
              e.id === id ? { ...e, customData: { ...e.customData, boardKind: 'comment', author: user.name || user.email, createdAt: new Date().toISOString() } } : e,
            );
      addElements(els.map((e) => ({ ...e, versionNonce: nonce(), seed: nonce() })), id);
      xapi.setActiveTool({ type: 'selection' });
      startEditingSelected();
    },
    [xapi, frameAt, addElements, startEditingSelected, user.name, user.email],
  );

  const armTool = useCallback(
    (tool: CustomTool) => {
      xapi?.setActiveTool({ type: 'custom', customType: tool });
    },
    [xapi],
  );

  const setPen = useCallback(
    (kind: 'pen' | 'highlighter') => {
      if (!xapi) return;
      xapi.updateScene({
        appState:
          kind === 'highlighter'
            ? { currentItemStrokeColor: '#fab005', currentItemOpacity: 40, currentItemStrokeWidth: 4 }
            : { currentItemStrokeColor: '#1e1e1e', currentItemOpacity: 100, currentItemStrokeWidth: 1 },
        captureUpdate: CaptureUpdateAction.NEVER,
      });
      xapi.setActiveTool({ type: 'freedraw' });
      setActiveTool(kind);
    },
    [xapi],
  );

  const zoomToFit = useCallback(() => {
    if (!xapi) return;
    const els = xapi.getSceneElements();
    if (els.length) xapi.scrollToContent(els, { fitToContent: true, animate: true });
  }, [xapi]);

  // ---- follow --------------------------------------------------------------------------
  const follow = useCallback(
    (u: PresenceUser | null) => {
      if (!xapi) return;
      if (!u || following === u.socketId) {
        xapi.updateScene({ appState: { userToFollow: null }, captureUpdate: CaptureUpdateAction.NEVER });
        setFollowing(null);
        return;
      }
      xapi.updateScene({ appState: { userToFollow: { socketId: u.socketId as SocketId, username: u.userName } }, captureUpdate: CaptureUpdateAction.NEVER });
      setFollowing(u.socketId);
      if (!collabRef.current?.jumpTo(u.socketId)) toast(`Following ${u.userName} — waiting for their cursor`);
    },
    [xapi, following],
  );

  // ---- design estimate: placing and editing fixtures ---------------------------------------
  /** Symbol size of the last fixture placed on a drawing, reused for fixtures placed off any drawing. */
  const lastFixtureScale = useRef(1);
  const placeFixture = useCallback(
    (x: number, y: number, opts: { target?: TakeoffTarget | null; qty?: number | null; length?: number | null } = {}) => {
      if (!xapi || readOnly) return;
      const t = targetFor(estimateRef.current, opts.target ?? armedRef.current);
      if (!t) {
        toast('Pick a fixture type first (Estimate panel, or 1–9 with the fixture tool on)', 'error');
        return;
      }
      const scene = xapi.getSceneElements() as unknown as SceneEl[];
      const scale = fixtureScaleAt(scene, x, y, lastFixtureScale.current);
      lastFixtureScale.current = scale;
      const els = fixtureElements(randomId(), t, x, y, { frameId: frameAt(x, y), scale, qty: opts.qty, length: opts.length });
      const restored = restoreElements(els as never, null, { refreshDimensions: true, repairBindings: true });
      xapi.updateScene({ elements: [...xapi.getSceneElementsIncludingDeleted(), ...restored], captureUpdate: CaptureUpdateAction.IMMEDIATELY });
    },
    [xapi, readOnly, frameAt],
  );

  /** Options → "Resize all to their drawings": standard symbol size for the drawing under each fixture. */
  const resizeFixtures = useCallback(() => {
    if (!xapi || readOnly) return;
    const all = xapi.getSceneElementsIncludingDeleted();
    const scene = xapi.getSceneElements() as unknown as SceneEl[];
    const byId = new Map(all.map((e) => [e.id, e]));
    const patch = new Map<string, Record<string, unknown>>();
    const rebuilt = new Map<string, unknown>();
    for (const e of scene) {
      const f = fixtureOf(e);
      if (e.isDeleted || f?.role !== 'fixture' || isRunLike(e)) continue;
      const t = targetFor(estimateRef.current, targetOfFixture(f));
      const { cx, cy, w: bw } = symbolBox(e);
      const k = fixtureScaleAt(scene, cx, cy, lastFixtureScale.current);
      const w = FIXTURE_W * k, h = FIXTURE_H * k;
      const texts = (e.boundElements ?? []).filter((b) => b.type === 'text');
      for (const b of texts) if (byId.get(b.id)) patch.set(b.id, { isDeleted: true });
      const want = t ? SYMBOL_KIND[t.symbol] : kindOfElement(e);
      if (t && (want === 'polygon' || want !== kindOfElement(e))) {
        // Redraw with the item's symbol (star / triangle / hexagon are polygons; old fixtures were ovals).
        const [fresh] = fixtureElements(e.id, t, cx, cy, { frameId: e.frameId ?? null, scale: k, qty: f.qty, length: f.length, placedAt: f.placed_at });
        const sameSize = kindOfElement(e) === want && Math.abs(w - bw) < Math.max(0.5, w * 0.1); // star / hexagon boxes are a bit narrower than the symbol size
        if (sameSize && !texts.length) continue;
        rebuilt.set(e.id, { ...fresh, customData: e.customData, version: (e.version ?? 1) + 2, groupIds: (byId.get(e.id) as { groupIds?: string[] } | undefined)?.groupIds ?? [] });
        continue;
      }
      if (Math.abs(w - e.width) < 0.5 && Math.abs(h - e.height) < 0.5 && !texts.length) continue;
      // Same-size circle / square for the drawing, and the old bound label removed (labels are an overlay now).
      patch.set(e.id, { x: cx - w / 2, y: cy - h / 2, width: w, height: h, boundElements: (e.boundElements ?? []).filter((b) => b.type !== 'text') });
    }
    for (const id of rebuilt.keys()) patch.set(id, {});
    if (!patch.size) {
      toast('Every fixture is already the standard size for its drawing');
      return;
    }
    const next = all.map((e) => (rebuilt.has(e.id) ? (rebuilt.get(e.id) as typeof e) : patch.has(e.id) ? (newElementWith(e as never, patch.get(e.id) as never) as typeof e) : e));
    const restored = restoreElements(next as never, null, { refreshDimensions: true, repairBindings: true });
    xapi.updateScene({ elements: restored, captureUpdate: CaptureUpdateAction.IMMEDIATELY });
    toast(`Resized ${[...patch.keys()].filter((id) => fixtureOf(byId.get(id) as never)?.role === 'fixture').length} fixtures to their drawings`);
  }, [xapi, readOnly]);

  /** W / A: Excalidraw's own line tool (click points, double-click / Enter to finish), kept on between runs. */
  const startMeasure = useCallback(
    (kind: 'linear' | 'area', target: TakeoffTarget | null = null) => {
      if (!xapi || readOnly) return;
      const m = { kind, target };
      setMirror(null);
      clearZone();
      if (!measureRef.current) roundnessRef.current = xapi.getAppState().currentItemRoundness;
      measureRef.current = m;
      setMeasuring(m);
      // Straight segments while measuring (Excalidraw's default "round" edges would curve an area).
      xapi.updateScene({ appState: { currentItemRoundness: 'sharp' }, captureUpdate: CaptureUpdateAction.NEVER });
      xapi.setActiveTool({ type: 'line', locked: true });
      // The banner over the canvas (MeasureOverlay) says how to finish, cancel and undo a point.
    },
    [xapi, readOnly],
  );

  const arm = useCallback(
    (t: TakeoffTarget | null) => {
      if (!xapi) return;
      // Runs and areas are drawn, not dropped: their types arm the measure tool instead.
      const ft = t ? targetFor(estimateRef.current, t) : null;
      if (t && ft && ft.kind === 'item' && (ft.measure === 'linear' || ft.measure === 'area')) {
        setArmed(null);
        startMeasure(ft.measure, t);
        return;
      }
      // Locked: Excalidraw resets unlocked tools to Select after each click; fixtures are placed many at a time.
      if (t) xapi.setActiveTool({ type: 'custom', customType: 'fixture', locked: true });
      else if (xapi.getAppState().activeTool.type === 'custom') xapi.setActiveTool({ type: 'selection' });
      setArmed(t);
    },
    [xapi, startMeasure],
  );

  /** Rewrite one element (fixture tag, colour, label) as a single undoable step. */
  const patchFixture = useCallback(
    (id: string, next: FixtureData | null, extra: Record<string, unknown> = {}) => {
      if (!xapi || readOnly) return;
      const els = xapi.getSceneElementsIncludingDeleted();
      const shape = els.find((e) => e.id === id) as unknown as SceneEl | undefined;
      if (!shape) return;
      const t = next ? targetFor(estimateRef.current, targetOfFixture(next)) : null;
      const textId = shape.boundElements?.find((b) => b.type === 'text')?.id;
      const text = t && next ? fixtureLabel(t.code, next, t.uom) : null;
      const out = els.map((e) => {
        if (e.id === id) {
          return newElementWith(e as never, { customData: { ...(e.customData ?? {}), estimate: next ?? undefined }, ...(t && !isRunLike(e as never) ? { strokeColor: t.color, backgroundColor: t.color } : t ? { strokeColor: t.color } : {}), ...extra } as never) as typeof e;
        }
        if (textId && e.id === textId && text !== null) {
          const changed = newElementWith(e as never, { text, originalText: text, strokeColor: t ? inkOn(t.color) : (e as { strokeColor: string }).strokeColor } as never);
          const [fixed] = restoreElements([changed] as never, null, { refreshDimensions: true }) as unknown as El[];
          return centreIn(fixed, shape as unknown as El) as unknown as typeof e;
        }
        return e;
      });
      xapi.updateScene({ elements: out, captureUpdate: CaptureUpdateAction.IMMEDIATELY });
    },
    [xapi, readOnly],
  );

  const setFixture = useCallback(
    (id: string, patch: { qty?: number | null; length?: number | null; area?: number | null }) => {
      if (!xapi) return;
      const el = xapi.getSceneElementsIncludingDeleted().find((e) => e.id === id) as unknown as SceneEl | undefined;
      const f = el && fixtureOf(el);
      if (!f) return;
      const next: FixtureData = { ...f };
      if (patch.qty !== undefined) {
        if (patch.qty && patch.qty !== 1) next.qty = patch.qty;
        else delete next.qty;
      }
      if (patch.length !== undefined) {
        if (patch.length && patch.length > 0) next.length = patch.length;
        else delete next.length;
      }
      if (patch.area !== undefined) {
        if (patch.area && patch.area > 0) next.area = patch.area;
        else delete next.area;
      }
      patchFixture(id, next);
    },
    [xapi, patchFixture],
  );

  const reassignFixture = useCallback(
    (id: string, target: TakeoffTarget) => {
      if (!xapi) return;
      const el = xapi.getSceneElementsIncludingDeleted().find((e) => e.id === id) as unknown as SceneEl | undefined;
      const f = el && fixtureOf(el);
      if (!f) return;
      const { item_id: _i, assembly_id: _a, ...rest } = f;
      const next: FixtureData = { ...rest, ...(target.kind === 'item' ? { item_id: target.id } : { assembly_id: target.id }) };
      patchFixture(id, next);
    },
    [xapi, patchFixture],
  );

  const assignLine = useCallback(
    (id: string, target: TakeoffTarget, along?: AlongRun) => {
      const el = xapi?.getSceneElements().find((e) => e.id === id) as unknown as GeomEl | undefined;
      const t = targetFor(estimateRef.current, target);
      if (along) {
        // Count along the run: an EACH type, ceil(length / one fixture's length) — see apps.takeoff.board.
        const prev = el ? fixtureOf(el as unknown as SceneEl) : null;
        patchFixture(id, { v: 1, role: 'fixture', item_id: target.id, placed_at: prev?.placed_at ?? Date.now(), measure: 'count', along_run: along }, { strokeWidth: 4 });
        return;
      }
      const measure = el && isAreaShape(el) && t?.measure === 'area' ? 'area' : 'linear';
      patchFixture(id, { v: 1, role: 'fixture', item_id: target.id, placed_at: Date.now(), measure }, { strokeWidth: 4 });
    },
    [xapi, patchFixture],
  );

  /** "Assign to item…" in the toolbar: runs count as LF/M items, closed shapes as SF/M2 items. */
  const assignKind = (id: string): 'linear' | 'area' => {
    const el = xapi?.getSceneElements().find((e) => e.id === id) as unknown as GeomEl | undefined;
    return el && isAreaShape(el) ? 'area' : 'linear';
  };
  const assignUoms = (kind: 'linear' | 'area') => (kind === 'area' ? ['SF', 'M2'] : ['LF', 'M']);
  const assignToItem = useCallback(
    async (id: string, choice: { itemId?: string; presetKey?: string; along?: AlongRun }) => {
      let itemId = choice.itemId;
      if (!itemId && choice.presetKey) {
        const preset = FIXTURE_PRESETS.find((x) => x.key === choice.presetKey);
        if (!preset) return;
        const row = await estimate.create<{ id: string; code?: string }>('items', { ...itemFromPreset(preset, {}, estimate.state?.pricing?.measure_display), code: nextTypeCode(preset.prefix, estimate.items) });
        if (!row) return;
        itemId = row.id;
        toast(`Added ${row.code ?? ''} ${preset.name} to the estimate`.replace(/\s+/g, ' '));
      }
      if (!itemId) return;
      assignLine(id, { kind: 'item', id: itemId }, choice.along);
      const row = choice.along ? estimate.items.find((i) => i.id === itemId) : null;
      if (row && choice.along && (row.run_unit_length !== choice.along.unit_length || row.run_unit !== choice.along.unit)) {
        void estimate.update('items', row, { run_unit_length: choice.along.unit_length, run_unit: choice.along.unit });
      }
    },
    [estimate, assignLine],
  );

  /** One element update (customData / lock / style) as a single step. */
  const patchElement = useCallback(
    (id: string, patch: Record<string, unknown>, capture: 'now' | 'never' = 'now') => {
      if (!xapi || readOnly) return;
      const out = xapi.getSceneElementsIncludingDeleted().map((e) => (e.id === id ? (newElementWith(e as never, patch as never) as typeof e) : e));
      xapi.updateScene({ elements: out, captureUpdate: capture === 'now' ? CaptureUpdateAction.IMMEDIATELY : CaptureUpdateAction.NEVER });
    },
    [xapi, readOnly],
  );

  /** A finished W / A line: close areas, tag it (fixture of the armed run / area type, or a plain measurement). */
  const finishMeasure = useCallback(
    (id: string) => {
      const m = measureRef.current;
      if (!xapi || !m) return;
      const el = xapi.getSceneElements().find((e) => e.id === id) as unknown as GeomEl | undefined;
      if (!el || el.isDeleted || el.type !== 'line' || (el.points?.length ?? 0) < 2) return;
      const pts = (el.points ?? []).map((q) => [q[0], q[1]] as [number, number]);
      const extra: Record<string, unknown> = {};
      if (m.kind === 'area') {
        if (pts.length < 3) return;
        if (!isLoop(el)) extra.points = [...pts, [pts[0][0], pts[0][1]]];
      }
      const t = m.target ? targetFor(estimateRef.current, m.target) : null;
      const color = t?.color ?? '#e8590c';
      const est = t
        ? { v: 1, role: 'fixture', item_id: t.id, placed_at: Date.now(), measure: m.kind }
        : { v: 1, role: 'measure', measure: m.kind, placed_at: Date.now() };
      // NEVER: the line's creation is the undo step; the tag rides along with it.
      patchElement(
        id,
        {
          ...extra,
          customData: { ...(el.customData ?? {}), estimate: est },
          strokeColor: color,
          strokeWidth: t ? 4 : 2,
          roughness: 0,
          roundness: null,
          ...(m.kind === 'area' ? { backgroundColor: color, fillStyle: 'hachure', opacity: 70 } : {}),
        },
        'never',
      );
    },
    [xapi, patchElement],
  );

  /** Tag a plain line / shape as a measurement (its length or area shows on the board). */
  const measureLine = useCallback(
    (id: string) => {
      const el = xapi?.getSceneElements().find((e) => e.id === id) as unknown as GeomEl | undefined;
      if (!el) return;
      patchElement(id, { customData: { ...(el.customData ?? {}), estimate: { v: 1, role: 'measure', measure: isAreaShape(el) ? 'area' : 'linear', placed_at: Date.now() } } });
    },
    [xapi, patchElement],
  );

  const imageEl = useCallback((id: string) => xapi?.getSceneElementsIncludingDeleted().find((e) => e.id === id) as unknown as GeomEl | undefined, [xapi]);

  /** Set (or clear) a drawing's scale. Setting locks it so it can't be nudged while measuring. */
  const setImageScale = useCallback(
    (id: string, scale: ImageScale | null) => {
      const el = imageEl(id);
      if (!el) return;
      const { scale: _old, ...rest } = (el.customData ?? {}) as Record<string, unknown>;
      if (!scale) {
        // The scale was what locked it: removing the scale unlocks the drawing too.
        patchElement(id, { customData: rest, locked: false });
        toast('Scale removed; drawing unlocked');
        return;
      }
      patchElement(id, { customData: { ...rest, scale: { ...scale, set_by: user.name || user.email, set_at: Date.now() } }, locked: true });
      setFocusImage(id);
      // A metric drawing on a feet project: switch the project to metres so new run / area types are M / M2.
      const display = estimateRef.current.state?.pricing?.measure_display ?? 'ftin';
      if (scale.unit === 'm' && display !== 'm') {
        void estimateRef.current.setPricing({ measure_display: 'm' });
        toast(`Scale ${scale.label}. Drawing locked. Project units switched to metres (Units button to change).`, 'info');
      } else {
        toast(`Scale ${scale.label}. Drawing locked; unlock from its 📏 badge.`, 'info');
      }
    },
    [imageEl, patchElement, user.name, user.email],
  );

  const toggleImageLock = useCallback(
    (id: string) => {
      const el = imageEl(id);
      if (!el) return;
      patchElement(id, { locked: !el.locked });
      toast(el.locked ? 'Unlocked — the scale stays with the drawing' : 'Locked');
    },
    [imageEl, patchElement],
  );

  const presetScale = useCallback(
    async (id: string, key: string) => {
      const el = imageEl(id);
      const preset = SCALE_PRESETS.find((p) => p.key === key);
      const src = el ? sourceOf(el) : null;
      if (!xapi || !el || !preset || !src?.page_pt) return;
      const nat = await naturalSize(xapi, el);
      if (!nat) return toast("Can't read the drawing's pixels yet — try again in a moment", 'error');
      setImageScale(id, scaleFromPreset(preset, src.page_pt, nat));
    },
    [xapi, imageEl, setImageScale],
  );

  /** ⇧C / "Set scale…": two clicks on the drawing, then the real distance. */
  const startCalibrate = useCallback(
    (id?: string | null) => {
      if (!xapi || readOnly) return;
      let target = id ?? focusImage ?? null;
      if (!target) {
        const st = xapi.getAppState();
        const sel = Object.keys(st.selectedElementIds).filter((k) => st.selectedElementIds[k]);
        const scene = xapi.getSceneElements() as unknown as GeomEl[];
        const picked = scene.find((e) => sel.includes(e.id) && e.type === 'image');
        const p = pointerRef.current;
        const under = p ? [...scene].reverse().find((e) => e.type === 'image' && !e.isDeleted && p.x >= e.x && p.x <= e.x + e.width && p.y >= e.y && p.y <= e.y + e.height) : undefined;
        target = picked?.id ?? under?.id ?? null;
      }
      xapi.setActiveTool({ type: 'selection' });
      if (target) setFocusImage(target);
      // No drawing chosen yet: the first click picks it (step 0 of the banner).
      setMirror(null);
      setZoneDraft(null);
      setCalibration({ imageId: target, pts: [] });
    },
    [xapi, readOnly, focusImage],
  );

  const calibrationDone = useCallback(
    async (len: { value: number; unit: 'ft' | 'm' }) => {
      const c = calibration;
      setCalibration(null);
      if (!xapi || !c || !c.imageId || c.pts.length < 2) return;
      const el = imageEl(c.imageId);
      if (!el) return;
      const nat = await naturalSize(xapi, el);
      if (!nat) return toast("Can't read the drawing's pixels yet — try again in a moment", 'error');
      const s = scaleFromCalibration(sceneToNatural(el, c.pts[0], nat), sceneToNatural(el, c.pts[1], nat), len, nat);
      if (!s) return toast('Pick two different points', 'error');
      setImageScale(c.imageId, s);
    },
    [xapi, calibration, imageEl, setImageScale],
  );

  const focusTarget = useCallback(
    (t: TakeoffTarget) => {
      if (!xapi) return;
      const els = xapi.getSceneElements().filter((e) => {
        const f = fixtureOf(e as unknown as SceneEl);
        return f && (f.item_id === t.id || f.assembly_id === t.id);
      });
      if (!els.length) return toast('None placed yet');
      xapi.updateScene({ appState: { selectedElementIds: Object.fromEntries(els.map((e) => [e.id, true])) as AppState['selectedElementIds'] }, captureUpdate: CaptureUpdateAction.NEVER });
      xapi.scrollToContent(els, { fitToContent: true, animate: true });
    },
    [xapi],
  );

  /** 1–9 with the fixture tool on: explicit favourite slots, else the Nth project type (hosts first). */
  const pickFavourite = useCallback(
    (slot: number): boolean => {
      if (!xapi) return false;
      const tool = xapi.getAppState().activeTool;
      if (!(tool.type === 'custom' && tool.customType === 'fixture')) return false;
      const e = estimateRef.current;
      const it = e.items.find((i) => i.favorite_slot === slot);
      const as = e.assemblies.find((a) => a.favorite_slot === slot);
      const anyFav = e.items.some((i) => i.favorite_slot) || e.assemblies.some((a) => a.favorite_slot);
      const all: TakeoffTarget[] = [...e.assemblies.map((a) => ({ kind: 'assembly' as const, id: a.id })), ...e.items.map((i) => ({ kind: 'item' as const, id: i.id }))];
      const t: TakeoffTarget | null = it ? { kind: 'item', id: it.id } : as ? { kind: 'assembly', id: as.id } : anyFav ? null : all[slot - 1] ?? null;
      if (!t) return true; // swallow: no type on that key
      arm(t);
      toast(`${e.targetInfo(t)?.label ?? 'Fixture'} — click to place`);
      return true;
    },
    [xapi, arm],
  );

  // ---- dimension lines ----------------------------------------------------------------------
  const startDimension = useCallback(() => {
    if (!xapi || readOnly) return;
    setCalibration(null);
    setArmed(null);
    setMirror(null);
    setZoneDraft(null);
    xapi.setActiveTool({ type: 'selection' });
    setDimension((d) => (d ? null : { pts: [] }));
  }, [xapi, readOnly]);

  /** Second click: a straight arrow with bar ends and a bound length label (role 'dimension'; never a quantity). */
  const placeDimension = useCallback(
    (a: [number, number], b: [number, number]) => {
      if (!xapi || readOnly || Math.hypot(b[0] - a[0], b[1] - a[1]) < 2) return;
      const id = `dim-${Math.random().toString(36).slice(2, 10)}`;
      const probe = { id, type: 'arrow', x: a[0], y: a[1], width: 0, height: 0, points: [[0, 0], [b[0] - a[0], b[1] - a[1]]] } as unknown as GeomEl;
      const scene = xapi.getSceneElements() as unknown as GeomEl[];
      const label = dimensionText(scene, probe, display);
      const mid: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      const frameId = frameAt(mid[0], mid[1]);
      const els = arrowElement(id, {
        points: [a, b],
        roundness: null,
        startArrowhead: 'bar',
        endArrowhead: 'bar',
        strokeColor: '#1e1e1e',
        strokeWidth: 1,
        roughness: 0,
        frameId,
        label,
        customData: { estimate: { v: 1, role: 'dimension', ends: 'bar', placed_at: Date.now() } },
        versionNonce: Math.floor(Math.random() * 2 ** 31),
        seed: Math.floor(Math.random() * 2 ** 31),
      });
      const restored = restoreElements(els as never, null, { refreshDimensions: true, repairBindings: true }) as unknown as El[];
      const [arrow, text] = restored;
      const placed = text ? [arrow, centreIn({ ...text, fontSize: 14 }, arrow)] : [arrow];
      xapi.updateScene({ elements: [...xapi.getSceneElementsIncludingDeleted(), ...(placed as never[])], captureUpdate: CaptureUpdateAction.IMMEDIATELY });
      xapi.updateScene({ appState: { selectedElementIds: { [id]: true } } as never, captureUpdate: CaptureUpdateAction.NEVER });
    },
    [xapi, readOnly, display, frameAt],
  );

  const dimensionPoint = useCallback(
    (pt: [number, number]) => {
      const d = dimension;
      if (!d) return;
      if (d.pts.length === 0) setDimension({ pts: [pt] });
      else {
        setDimension(null);
        placeDimension(d.pts[0], pt);
      }
    },
    [dimension, placeDimension],
  );

  // ---- mirror ---------------------------------------------------------------------------------
  const startMirror = useCallback(() => {
    if (!xapi || readOnly) return;
    if (mirror) {
      setMirror(null);
      return;
    }
    const st = xapi.getAppState();
    const ids = Object.keys(st.selectedElementIds).filter((k) => st.selectedElementIds[k]);
    const live = new Set(xapi.getSceneElements().map((e) => e.id));
    const sel = ids.filter((id) => live.has(id));
    if (!sel.length) {
      toast('Select what to mirror first, then Mirror (⇧M)', 'error');
      return;
    }
    setCalibration(null);
    setArmed(null);
    setDimension(null);
    setZoneDraft(null);
    xapi.setActiveTool({ type: 'selection' });
    setMirror({ ids: sel, pts: [] });
  }, [xapi, readOnly, mirror]);

  /** Apply: duplicate (keep original) or flip in place, as one undo step; selects the result. */
  const applyMirror = useCallback(
    (ids: string[], axis: { p: [number, number]; q: [number, number] }) => {
      if (!xapi || readOnly) return;
      const all = xapi.getSceneElementsIncludingDeleted();
      const r = mirrorSelection(all as unknown as MEl[], ids, axis, { duplicate: mirrorKeep });
      if (!r.added.length && !r.changed.length) return;
      const changed = new Map(r.changed.map((e) => [e.id, e]));
      const added = r.added.length ? (restoreElements(r.added as never, null, { refreshDimensions: false, repairBindings: false }) as unknown as typeof all) : [];
      const next = [...all.map((e) => (changed.has(e.id) ? (changed.get(e.id) as unknown as typeof e) : e)), ...added];
      xapi.updateScene({ elements: next, captureUpdate: CaptureUpdateAction.IMMEDIATELY });
      xapi.updateScene({ appState: { selectedElementIds: Object.fromEntries(r.select.map((id) => [id, true])) } as never, captureUpdate: CaptureUpdateAction.NEVER });
      const n = r.select.length;
      toast(mirrorKeep ? `Mirrored ${n} item${n === 1 ? '' : 's'} (copies)` : `Flipped ${n} item${n === 1 ? '' : 's'} in place`);
    },
    [xapi, readOnly, mirrorKeep],
  );

  const mirrorPoint = useCallback(
    (pt: [number, number], alt: boolean) => {
      const m = mirror;
      if (!m) return;
      if (m.pts.length === 0 && alt) {
        setMirror(null);
        applyMirror(m.ids, verticalAxis(pt));
        return;
      }
      if (m.pts.length === 0) {
        setMirror({ ...m, pts: [pt] });
        return;
      }
      const axis = axisFrom(m.pts[0], pt);
      if (!axis) return; // same point twice: wait for a real second point
      setMirror(null);
      applyMirror(m.ids, axis);
    },
    [mirror, applyMirror],
  );

  /** Ends: bars (dimension ticks) or arrowheads. */
  const setDimensionEnds = useCallback(
    (id: string, ends: 'bar' | 'arrow') => {
      const el = xapi?.getSceneElements().find((e) => e.id === id) as unknown as GeomEl | undefined;
      const d = el && dimensionOf(el);
      if (!el || !d) return;
      patchElement(id, { startArrowhead: ends, endArrowhead: ends, customData: { ...(el.customData ?? {}), estimate: { ...d, ends } } });
    },
    [xapi, patchElement]
  );

  const deleteElement = useCallback(
    (id: string) => {
      if (!xapi || readOnly) return;
      const el = xapi.getSceneElementsIncludingDeleted().find((e) => e.id === id) as unknown as SceneEl | undefined;
      const ids = new Set([id, ...(el?.boundElements ?? []).filter((b) => b.type === 'text').map((b) => b.id)]);
      const out = xapi.getSceneElementsIncludingDeleted().map((e) => (ids.has(e.id) ? (newElementWith(e as never, { isDeleted: true } as never) as typeof e) : e));
      xapi.updateScene({ elements: out, appState: { selectedElementIds: {} } as never, captureUpdate: CaptureUpdateAction.IMMEDIATELY });
    },
    [xapi, readOnly],
  );

  // ---- zones ---------------------------------------------------------------------------------
  const startZone = useCallback(() => {
    if (!xapi || readOnly) return;
    if (zoneRef.current) {
      // Zone again while drawing one: close it (3+ points → name it) or drop it.
      zoneInterrupt(() => undefined);
      return;
    }
    setCalibration(null);
    setArmed(null);
    setDimension(null);
    setMirror(null);
    xapi.setActiveTool({ type: 'selection' });
    xapi.updateScene({ appState: { selectedElementIds: {} } as never, captureUpdate: CaptureUpdateAction.NEVER });
    setZone({ pts: [], dragging: false, naming: false, defaultName: nextZoneName(xapi.getSceneElements() as unknown as GeomEl[]) });
  }, [xapi, readOnly, zoneInterrupt, setZone]);

  /** Add the closed zone (one undo step), under the fixtures so they stay clickable. */
  const commitZone = useCallback(
    (pts: [number, number][], name: string) => {
      const after = zonePending.current;
      clearZone();
      // The tool asked for while the zone was open starts once it's named.
      if (after) setTimeout(after, 0);
      if (!xapi || readOnly || pts.length < 3) return;
      const all = xapi.getSceneElementsIncludingDeleted();
      const colour = ZONE_COLOURS[zonesOf(all as unknown as GeomEl[]).length % ZONE_COLOURS.length];
      const el = {
        ...polygonElement(randomId(), {
          points: pts,
          strokeColor: colour,
          backgroundColor: `${colour}26`, // ~15 % fill
          fillStyle: 'solid',
          strokeWidth: 2,
          strokeStyle: 'dashed',
          roughness: 0,
          roundness: null,
          customData: { estimate: { v: 1, role: 'zone', name: name.toUpperCase() } },
        } as never),
        polygon: true,
      };
      const [restored] = restoreElements([el] as never, null, { refreshDimensions: true, repairBindings: false });
      const firstFixture = all.findIndex((e) => !e.isDeleted && fixtureOf(e as unknown as SceneEl));
      const next = firstFixture < 0 ? [...all, restored] : [...all.slice(0, firstFixture), restored, ...all.slice(firstFixture)];
      xapi.updateScene({ elements: next as never, captureUpdate: CaptureUpdateAction.IMMEDIATELY });
      toast(`Zone ${name.toUpperCase()} added — fixtures inside it go in that ZONE in the workbook`);
    },
    [xapi, readOnly, clearZone],
  );

  /**
   * Keys while the Zone tool is on (the keymap's intercept, so it runs before the keymap and Excalidraw).
   * Drawing: Esc cancels, Enter closes (3+ points), Backspace / ⌘Z drops the last point, Space pans; any
   * other key is a tool — 3+ points → name the zone first, then the key is replayed; fewer → the zone is
   * dropped and the key works as usual. Naming: Enter adds it, Esc keeps it with its default name, nothing
   * else on the board responds.
   */
  const zoneKey = useCallback(
    (e: KeyboardEvent): boolean => {
      const d = zoneRef.current;
      if (!d || e.isComposing) return false;
      const k = e.key;
      if (k === 'Shift' || k === 'Alt' || k === 'Control' || k === 'Meta' || k === 'CapsLock') return false;
      const t = e.target as HTMLElement | null;
      const inForm = !!t?.closest?.('.xb-zone-name');
      const editable = !!t && (t.isContentEditable || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT');
      const mod = e.metaKey || e.ctrlKey;
      const stop = (prevent = true) => {
        if (prevent) e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return true;
      };
      const nudge = () => setZone({ ...d, nudge: (d.nudge ?? 0) + 1 });
      if (d.naming) {
        if (k === 'Escape') {
          commitZone(d.pts, d.defaultName);
          return stop();
        }
        if (inForm) return false; // typing the name; Enter submits the form
        if (k === 'Enter' && !editable) {
          // Enter on the board confirms the name; Enter in another field (quick add…) is paused below.
          document.querySelector<HTMLFormElement>('.xb-zone-name')?.requestSubmit();
          return stop();
        }
        nudge();
        return stop(!mod); // ⌘R etc. still reach the browser
      }
      if (editable) return false; // the panel's fields; quick-add placement is paused by zoneInterrupt
      if (k === 'Escape') {
        clearZone();
        return stop();
      }
      if (k === 'Enter') {
        if (d.pts.length >= 3) setZone({ ...d, dragging: false, naming: true });
        else if (d.pts.length) nudge();
        else clearZone();
        return stop();
      }
      if (k === 'Backspace' || k === 'Delete' || (mod && k.toLowerCase() === 'z' && !e.shiftKey)) {
        if (d.pts.length) setZone({ ...d, pts: d.pts.slice(0, -1) });
        return stop();
      }
      if (k === ' ' || k.startsWith('Arrow') || k === 'Tab' || /^F\d+$/.test(k)) return false;
      if (mod) return stop(false);
      if (e.repeat) return stop();
      if (k.toLowerCase() === 'z' && !e.altKey) {
        // Z again: close it (3+ points) or drop it — never a second zone on top.
        zoneInterrupt(() => undefined);
        return stop();
      }
      if (d.pts.length >= 3) {
        const init: KeyboardEventInit = { key: e.key, code: e.code, shiftKey: e.shiftKey, altKey: e.altKey, bubbles: true, cancelable: true };
        zoneInterrupt(() => document.body.dispatchEvent(new KeyboardEvent('keydown', init)));
        return stop();
      }
      clearZone(); // fewer than 3 points: drop it, the key does what it always does
      return false;
    },
    [commitZone, clearZone, setZone, zoneInterrupt],
  );

  const renameZone = useCallback(
    (id: string, name: string) => {
      const el = xapi?.getSceneElements().find((e) => e.id === id);
      const n = name.trim().toUpperCase();
      if (!el || !n) return;
      const cd = (el.customData ?? {}) as Record<string, unknown>;
      patchElement(id, { customData: { ...cd, estimate: { ...((cd.estimate as Record<string, unknown>) ?? {}), v: 1, role: 'zone', name: n } } });
    },
    [xapi, patchElement],
  );

  /** SCOPE of a drawn zone or a placed fixture: customData.estimate.scope (blank = inherit), one undo step. */
  const setEstimateScope = useCallback(
    (id: string, scope: string) => {
      const el = xapi?.getSceneElements().find((e) => e.id === id);
      if (!el) return;
      const cd = (el.customData ?? {}) as Record<string, unknown>;
      const est = { ...((cd.estimate as Record<string, unknown>) ?? {}) };
      const v = scope.trim();
      if ((String(est.scope ?? '').trim()) === v) return;
      if (v) est.scope = v;
      else delete est.scope;
      patchElement(id, { customData: { ...cd, estimate: est } });
    },
    [xapi, patchElement],
  );

  /**
   * Hide / show drawn zones ("set and forget"), board-wide and synced: a hidden zone is invisible (opacity 0)
   * and locked (not clickable) but still counts for ZONE. Its own opacity / lock are kept in
   * estimate.hidden_from and restored exactly on show. Points are untouched, so the zone signature (and the
   * workbook rows) don't change. All the zones in one undo step.
   */
  const setZonesHidden = useCallback(
    (ids: string[], hidden: boolean) => {
      if (!xapi || readOnly || !ids.length) return;
      const want = new Set(ids);
      let changed = false;
      const out = xapi.getSceneElementsIncludingDeleted().map((e) => {
        if (!want.has(e.id) || e.isDeleted || !zoneOf(e)) return e;
        const cd = (e.customData ?? {}) as Record<string, unknown>;
        const est = { ...((cd.estimate as Record<string, unknown>) ?? {}) };
        if (!!est.hidden === hidden) return e;
        changed = true;
        if (hidden) {
          est.hidden = true;
          est.hidden_from = { opacity: e.opacity, locked: e.locked };
          return newElementWith(e as never, { opacity: 0, locked: true, customData: { ...cd, estimate: est } } as never) as typeof e;
        }
        const from = (est.hidden_from ?? {}) as { opacity?: number; locked?: boolean };
        delete est.hidden;
        delete est.hidden_from;
        return newElementWith(e as never, { opacity: typeof from.opacity === 'number' ? from.opacity : 100, locked: !!from.locked, customData: { ...cd, estimate: est } } as never) as typeof e;
      });
      if (!changed) return;
      const sel = { ...xapi.getAppState().selectedElementIds } as Record<string, boolean>;
      if (hidden) for (const id of ids) delete sel[id];
      xapi.updateScene({ elements: out, appState: { selectedElementIds: sel as AppState['selectedElementIds'] }, captureUpdate: CaptureUpdateAction.IMMEDIATELY });
    },
    [xapi, readOnly],
  );

  /** ⌖ on a zone: select and zoom to the fixtures inside it. */
  const focusZone = useCallback(
    (id: string) => {
      if (!xapi) return;
      const scene = xapi.getSceneElements();
      const ids = fixturesByZone(scene as unknown as GeomEl[]).get(id) ?? [];
      const els = scene.filter((e) => ids.includes(e.id));
      if (!els.length) return toast('No fixtures in this zone yet');
      xapi.updateScene({ appState: { selectedElementIds: Object.fromEntries(els.map((e) => [e.id, true])) as AppState['selectedElementIds'] }, captureUpdate: CaptureUpdateAction.NEVER });
      xapi.scrollToContent(els, { fitToContent: true, animate: true });
    },
    [xapi],
  );

  const keyEstimate = useMemo(
    () => ({
      arm: () => {
        const e = estimateRef.current;
        const t = armedRef.current ?? (e.assemblies[0] ? { kind: 'assembly' as const, id: e.assemblies[0].id } : e.items[0] ? { kind: 'item' as const, id: e.items[0].id } : null);
        if (!t) {
          setEstimateOpen(true);
          writeFlag(estimateKey(boardId), '1');
          toast('Add a fixture type from the library first', 'error');
          return;
        }
        arm(t);
      },
      quickAdd: () => {
        setEstimateOpen(true);
        writeFlag(estimateKey(boardId), '1');
        requestAnimationFrame(() => requestAnimationFrame(() => quickAddRef.current?.focus()));
      },
      togglePanel: () =>
        setEstimateOpen((v) => {
          writeFlag(estimateKey(boardId), v ? '0' : '1');
          return !v;
        }),
      favourite: pickFavourite,
      measure: (kind: 'linear' | 'area') => {
        // A run / area type already armed (or the last one picked) measures as that type.
        const a = armedRef.current ?? measureRef.current?.target ?? null;
        const ft = a ? targetFor(estimateRef.current, a) : null;
        startMeasure(kind, ft && ft.measure === kind ? a : null);
      },
      calibrate: () => startCalibrate(),
      dimension: () => startDimension(),
      mirror: () => startMirror(),
      zone: () => startZone(),
      cancelTool: () => {
        if (zoneRef.current) {
          clearZone();
          return true;
        }
        if (!mirrorRef.current) return false;
        setMirror(null);
        return true;
      },
    }),
    [arm, pickFavourite, boardId, startMeasure, startCalibrate, startDimension, startMirror, startZone, clearZone],
  );


  // ---- measure & estimate toolbar ----------------------------------------------------------
  /** Set scale with nothing chosen: the first click picks the drawing under it. */
  const calibrationPoint = useCallback(
    (pt: [number, number]) => {
      setCalibration((c) => {
        if (!c) return c;
        if (!c.imageId) {
          const scene = (xapi?.getSceneElements() ?? []) as unknown as GeomEl[];
          const hit = [...scene].reverse().find((e) => e.type === 'image' && !e.isDeleted && pt[0] >= e.x && pt[0] <= e.x + e.width && pt[1] >= e.y && pt[1] <= e.y + e.height);
          if (!hit) {
            toast('Click on a drawing to scale it (Esc cancels)', 'error');
            return c;
          }
          setFocusImage(hit.id);
          return { imageId: hit.id, pts: [] };
        }
        return c.pts.length < 2 ? { ...c, pts: [...c.pts, pt] } : c;
      });
    },
    [xapi],
  );

  const toggleCurve = useCallback(
    (id: string) => {
      const el = xapi?.getSceneElements().find((e) => e.id === id);
      if (!el) return;
      patchElement(id, { roundness: el.roundness ? null : { type: ROUNDNESS.PROPORTIONAL_RADIUS } });
    },
    [xapi, patchElement],
  );

  const editPoints = useCallback(
    (id: string) => {
      if (!xapi) return;
      const st = xapi.getAppState();
      if (st.editingLinearElement?.elementId === id) return;
      if (!st.selectedElementIds[id]) xapi.updateScene({ appState: { selectedElementIds: { [id]: true } as AppState['selectedElementIds'] }, captureUpdate: CaptureUpdateAction.NEVER });
      if (st.activeTool.type !== 'selection') xapi.setActiveTool({ type: 'selection' });
      requestAnimationFrame(enterPointEditor);
    },
    [xapi],
  );

  const setDblSettings = useCallback((next: DblClickSettings) => {
    setDbl(next);
    try {
      localStorage.setItem(DBL_KEY, JSON.stringify(next));
    } catch {
      /* private window */
    }
  }, []);

  // W / A while a run is being drawn: Esc cancels it (Excalidraw would keep it), Backspace drops the last point.
  useEffect(() => {
    if (!xapi || !measuring) return;
    const key = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' && e.key !== 'Backspace' && e.key !== 'Delete') return;
      if (isTypingTarget(e.target)) return;
      const st = xapi.getAppState();
      const el = (st.multiElement ?? (st.newElement?.type === 'line' ? st.newElement : null)) as (ExcalidrawElement & { points: readonly [number, number][] }) | null;
      if (!el || el.type !== 'line') return; // not mid-run: Esc leaves the tool as usual
      e.preventDefault();
      e.stopPropagation();
      const cancel = () => {
        mutateElement(el as never, { isDeleted: true } as never);
        draftRef.current = null;
        setDraftId(null);
        xapi.updateScene({ appState: { multiElement: null, newElement: null, selectedElementIds: {}, selectedLinearElement: null } as never, captureUpdate: CaptureUpdateAction.NEVER });
      };
      if (e.key === 'Escape') {
        cancel();
        toast(measuring.kind === 'area' ? 'Area cancelled' : 'Run cancelled');
        return;
      }
      const next = removeLastVertex(el.points, (el as unknown as { lastCommittedPoint?: [number, number] | null }).lastCommittedPoint);
      if (!next) return cancel();
      mutateElement(el as never, { points: next.points, lastCommittedPoint: next.lastCommitted } as never);
      xapi.updateScene({ appState: {}, captureUpdate: CaptureUpdateAction.NEVER });
    };
    window.addEventListener('keydown', key, true);
    return () => window.removeEventListener('keydown', key, true);
  }, [xapi, measuring]);

  // Double-click a line (and measured runs) → its point editor, not a text label; arrows keep labels by default.
  useEffect(() => {
    if (!xapi || readOnly) return;
    const host = document.querySelector('.xb-canvas .excalidraw') as HTMLElement | null;
    if (!host) return;
    const dbl = (ev: MouseEvent) => {
      const t = ev.target as HTMLElement | null;
      if (!t || t.tagName !== 'CANVAS' || ev.metaKey || ev.ctrlKey) return;
      const st = xapi.getAppState();
      if (st.activeTool.type !== 'selection' || st.multiElement || st.editingLinearElement || st.editingTextElement) return;
      const ids = Object.keys(st.selectedElementIds).filter((k) => st.selectedElementIds[k]);
      if (ids.length !== 1) return;
      const el = xapi.getSceneElements().find((e) => e.id === ids[0]) as unknown as (GeomEl & { elbowed?: boolean; strokeWidth?: number }) | undefined;
      if (!el) return;
      const f = fixtureOf(el as unknown as SceneEl);
      const measured = !!measureOf(el) || !!dimensionOf(el) || !!(f && f.measure === 'linear');
      if (!dblClickEditsPoints(el, measured, dblRef.current)) return;
      const r = host.getBoundingClientRect();
      const z = st.zoom.value;
      const p: [number, number] = [(ev.clientX - r.left) / z - st.scrollX, (ev.clientY - r.top) / z - st.scrollY];
      if (distanceToPolyline(scenePoints(el), p) > Math.max(12 / z, (el.strokeWidth ?? 2) * 2)) return; // double-click elsewhere: text, as usual
      ev.preventDefault();
      ev.stopPropagation();
      enterPointEditor();
    };
    host.addEventListener('dblclick', dbl, true);
    return () => host.removeEventListener('dblclick', dbl, true);
  }, [xapi, readOnly, ready]);

  // Our activity next to our cursor on collaborators' screens.
  useEffect(() => {
    const info = armed ? estimate.targetInfo(armed) : null;
    const mInfo = measuring?.target ? estimate.targetInfo(measuring.target) : null;
    collabRef.current?.setLabel(
      info ? `I ${info.label}` : measuring ? `${measuring.kind === 'area' ? 'A' : 'W'} ${mInfo?.label ?? (measuring.kind === 'area' ? 'measuring an area' : 'measuring a run')}` : calibration ? 'Setting a scale' : '',
    );
  }, [armed, measuring, calibration, estimate.targetInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // The image card follows the selection; a 📏 badge click shows a locked drawing's card.
  useEffect(() => {
    if (selection.key) setFocusImage(selection.image);
  }, [selection.key, selection.image]);

  // Dropping a PDF: each page becomes a drawing image (with its sheet size, for printed-scale presets).
  useEffect(() => {
    if (!xapi || readOnly) return;
    const host = document.querySelector('.xb-canvas') as HTMLElement | null;
    if (!host) return;
    const pdfs = (dt: DataTransfer | null) => Array.from(dt?.files ?? []).filter((f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name));
    const over = (ev: DragEvent) => {
      if (Array.from(ev.dataTransfer?.items ?? []).some((i) => i.type === 'application/pdf')) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    };
    const drop = (ev: DragEvent) => {
      const files = pdfs(ev.dataTransfer);
      if (!files.length) return;
      ev.preventDefault();
      ev.stopPropagation();
      const st = xapi.getAppState();
      const r = host.getBoundingClientRect();
      const at = { x: (ev.clientX - r.left) / st.zoom.value - st.scrollX, y: (ev.clientY - r.top) / st.zoom.value - st.scrollY };
      void (async () => {
        for (const f of files) {
          toast(`Rendering ${f.name}…`);
          try {
            const n = await dropPdf(xapi, f, at, { frameAt });
            toast(`${f.name}: ${n} page${n === 1 ? '' : 's'} added — select a page to set its scale`, 'info');
            at.y += 1400;
          } catch (e) {
            toast(`Couldn't read ${f.name}: ${(e as Error).message}`, 'error');
          }
        }
      })();
    };
    host.addEventListener('dragover', over, true);
    host.addEventListener('drop', drop, true);
    return () => {
      host.removeEventListener('dragover', over, true);
      host.removeEventListener('drop', drop, true);
    };
  }, [xapi, readOnly, frameAt]);

  // Dimension labels follow the scene (ends moved, drawing rescaled / resized, units, remote edits).
  // Not undoable (CaptureUpdateAction.NEVER): only the user's own edit is; every editor computes the
  // same text, so concurrent refreshes converge instead of ping-ponging.
  useEffect(() => {
    if (!xapi || readOnly) return;
    const els = xapi.getSceneElementsIncludingDeleted();
    const live = els.filter((e) => !e.isDeleted) as unknown as GeomEl[];
    const byId = new Map(els.map((e) => [e.id, e]));
    const fixes = new Map<string, { text: string; container: string }>();
    for (const e of live) {
      if (!dimensionOf(e)) continue;
      const textId = (e as unknown as SceneEl).boundElements?.find((b) => b.type === 'text')?.id;
      const t = textId ? (byId.get(textId) as unknown as { text?: string; isDeleted?: boolean } | undefined) : undefined;
      if (!t || t.isDeleted) continue;
      const want = dimensionText(live, e, display);
      if (t.text !== want) fixes.set(textId!, { text: want, container: e.id });
    }
    if (!fixes.size) return;
    const out = els.map((e) => {
      const fix = fixes.get(e.id);
      if (!fix) return e;
      const changed = newElementWith(e as never, { text: fix.text, originalText: fix.text } as never);
      const [fixed] = restoreElements([changed] as never, null, { refreshDimensions: true }) as unknown as El[];
      return centreIn(fixed, byId.get(fix.container) as unknown as El) as unknown as typeof e;
    });
    xapi.updateScene({ elements: out, captureUpdate: CaptureUpdateAction.NEVER });
  }, [xapi, readOnly, sceneKey, display]);

  // Catalog edits (code, colour, UOM) → keep placed fixtures' labels and colours in step.
  useEffect(() => {
    if (!xapi || readOnly || !estimate.state) return;
    const els = xapi.getSceneElementsIncludingDeleted();
    const byId = new Map(els.map((e) => [e.id, e as unknown as SceneEl]));
    const fixes = new Map<string, Record<string, unknown>>();
    for (const e of els as unknown as SceneEl[]) {
      const f = fixtureOf(e);
      if (!f) continue;
      const t = targetFor(estimate, targetOfFixture(f));
      if (!t) continue;
      if (!isRunLike(e as never) && (e.backgroundColor !== t.color || e.strokeColor !== t.color)) fixes.set(e.id, { strokeColor: t.color, backgroundColor: t.color });
      else if (isRunLike(e as never) && e.strokeColor !== t.color) fixes.set(e.id, { strokeColor: t.color }); // runs: line colour only
      const textId = e.boundElements?.find((b) => b.type === 'text')?.id;
      const text = textId ? byId.get(textId) : null;
      const want = fixtureLabel(t.code, f, t.uom);
      if (text && !text.isDeleted && text.text !== want) fixes.set(text.id, { text: want, originalText: want, strokeColor: inkOn(t.color), __container: e.id });
    }
    if (!fixes.size) return;
    const out = els.map((e) => {
      const fix = fixes.get(e.id);
      if (!fix) return e;
      const { __container, ...patch } = fix as Record<string, unknown> & { __container?: string };
      const changed = newElementWith(e as never, patch as never);
      if (!__container) return changed as typeof e;
      const [fixed] = restoreElements([changed] as never, null, { refreshDimensions: true }) as unknown as El[];
      return centreIn(fixed, byId.get(__container) as unknown as El) as unknown as typeof e;
    });
    xapi.updateScene({ elements: out, captureUpdate: CaptureUpdateAction.NEVER });
  }, [xapi, readOnly, estimate.state]); // eslint-disable-line react-hooks/exhaustive-deps

  const trackEstimate = useCallback((els: readonly OrderedExcalidrawElement[], appState: AppState) => {
    const scene = els as unknown as SceneEl[];
    const sig = estimateSignature(scene);
    if (sig !== estimateSig.current) {
      const first = estimateSig.current === null;
      estimateSig.current = sig;
      if (!first) estimateRef.current.refreshQuantities(500);
    }
    // Selection → the panel's fixture / frame / line card.
    const ids = Object.keys(appState.selectedElementIds).filter((k) => appState.selectedElementIds[k]);
    let el = ids.length === 1 ? scene.find((e) => e.id === ids[0]) : undefined;
    if (el?.containerId) el = scene.find((e) => e.id === el!.containerId) ?? el;
    const key = el ? `${el.id}:${el.version}` : '';
    setSelection((prev) => {
      if (prev.key === key) return prev;
      if (!el || el.isDeleted) return { fixture: null, frame: null, line: null, image: null, zone: null, key };
      if (zoneOf(el)) return { fixture: null, frame: null, line: null, image: null, zone: el.id, key };
      const f = fixtureOf(el);
      const target = f ? targetOfFixture(f) : null;
      if (f && target) {
        return {
          key,
          frame: null,
          line: null,
          image: null,
          fixture: { elementId: el.id, target, measure: f.measure ?? 'count', qty: f.qty ?? 1, length: f.length ?? null, area: f.area ?? null, pxLength: isRunLike(el) ? pxLength(el) : null, frameId: el.frameId ?? null, along: f.measure === 'count' ? f.along_run ?? null : null, scope: String(f.scope ?? '').trim() },
        };
      }
      if (el.type === 'frame' || el.type === 'magicframe') {
        const fs = framesOf(scene);
        return { key, fixture: null, line: null, image: null, frame: { frameId: el.id, name: frameName(el, fs.findIndex((x) => x.id === el!.id)) } };
      }
      if (el.type === 'image') return { key, fixture: null, frame: null, line: null, image: el.id };
      if (isRunLike(el) || ((el.type === 'rectangle' || el.type === 'ellipse' || el.type === 'diamond') && measureOf(el as unknown as GeomEl))) {
        const fs = framesOf(scene);
        const fr = (el.frameId && fs.find((x) => x.id === el!.frameId)) || frameAtPoint(scene, el.x + (el.points?.[0]?.[0] ?? 0), el.y + (el.points?.[0]?.[1] ?? 0));
        return { key, fixture: null, frame: null, image: null, line: { elementId: el.id, px: pxLength(el), frameId: fr ? fr.id : null, frameName: fr ? frameName(fr, fs.indexOf(fr)) : '' } };
      }
      return { fixture: null, frame: null, line: null, image: null, key };
    });
  }, []);

  // ---- Excalidraw callbacks --------------------------------------------------------------
  const onChange = useCallback((els: readonly OrderedExcalidrawElement[], appState: AppState, files: BinaryFiles) => {
    lastScene.current = { elements: els, appState, files };
    collabRef.current?.handleChange(files);
    if (appState.theme !== themeRef.current) {
      themeRef.current = appState.theme;
      setTheme(appState.theme);
      try {
        localStorage.setItem(THEME_KEY, appState.theme);
      } catch {
        /* ignore */
      }
    }
    trackEstimate(els, appState);
    sceneKeyRef.current = viewKey(els, appState);
    if (tickRaf.current === null) {
      tickRaf.current = requestAnimationFrame(() => {
        tickRaf.current = null;
        setSceneKey(sceneKeyRef.current);
        camera.bump();
      });
    }
    const t = appState.activeTool;
    // Another Excalidraw tool picked (its toolbar) while a zone is open: name / drop the zone first.
    if (zoneRef.current && t.type !== 'selection' && t.type !== 'hand') {
      const want = t.type === 'custom' ? { type: 'custom' as const, customType: t.customType } : { type: t.type };
      const d = zoneRef.current;
      setTimeout(() => {
        const x = xapiRef.current;
        if (!x) return;
        if (d.naming || d.pts.length >= 3) x.setActiveTool({ type: 'selection' });
        zoneInterruptRef.current(() => x.setActiveTool(want as never));
      }, 0);
    }
    // W / A: follow the line Excalidraw is drawing; tag it once Excalidraw lets go of it.
    if (measureRef.current) {
      const drawing = (appState.multiElement ?? appState.newElement) as { id: string; type: string } | null;
      if (drawing && drawing.type === 'line') {
        if (draftRef.current !== drawing.id) {
          draftRef.current = drawing.id;
          setDraftId(drawing.id);
        }
      } else if (draftRef.current) {
        const id = draftRef.current;
        draftRef.current = null;
        setDraftId(null);
        setTimeout(() => finishMeasureRef.current(id), 0);
      }
      if (t.type !== 'line' && !drawing) {
        measureRef.current = null;
        setMeasuring(null);
        const prev = roundnessRef.current;
        roundnessRef.current = null;
        if (prev && prev !== appState.currentItemRoundness) {
          setTimeout(() => xapiRef.current?.updateScene({ appState: { currentItemRoundness: prev }, captureUpdate: CaptureUpdateAction.NEVER }), 0);
        }
      }
    }
    if (armedRef.current && !(t.type === 'custom' && t.customType === 'fixture')) {
      armedRef.current = null;
      setArmed(null);
    }
    const toolName = t.type === 'custom' ? t.customType : t.type;
    setActiveTool((prev) => (prev === toolName || (toolName === 'freedraw' && (prev === 'pen' || prev === 'highlighter')) ? prev : toolName));
    const f = appState.userToFollow?.socketId ?? null;
    setFollowing((prev) => (prev === f ? prev : f));
    if (!fitTimer.current) {
      fitTimer.current = setTimeout(() => {
        fitTimer.current = null;
        fitterRef.current?.run();
      }, 30);
    }
  }, [trackEstimate, camera]);
  const finishMeasureRef = useRef(finishMeasure);
  finishMeasureRef.current = finishMeasure;

  const onPointerUpdate = useCallback(
    (p: { pointer: { x: number; y: number; tool: 'pointer' | 'laser' }; button: 'down' | 'up' }) => {
      collabRef.current?.sendPointer(p.pointer, p.button);
      pointerRef.current = { x: p.pointer.x, y: p.pointer.y };
      const fitter = fitterRef.current;
      if (fitter) {
        const was = fitter.pointerDown;
        fitter.pointerDown = p.button === 'down';
        if (was && !fitter.pointerDown) setTimeout(() => fitter.run(), 30);
      }
    },
    [],
  );

  const onPointerDown = useCallback(
    (tool: AppState['activeTool'], state: { origin: { x: number; y: number } }) => {
      if (tool.type === 'custom' && (tool.customType === 'sticky' || tool.customType === 'triangle' || tool.customType === 'star')) {
        placeCustom(tool.customType, state.origin.x, state.origin.y);
      }
      if (tool.type === 'custom' && tool.customType === 'fixture') placeFixture(state.origin.x, state.origin.y);
    },
    [placeCustom, placeFixture],
  );

  const initialData = useMemo(
    () => ({
      appState: {
        viewBackgroundColor: '#ffffff',
        theme: readTheme(),
        currentItemRoughness: readClean() ? 0 : 1,
        currentItemFontFamily: (readClean() ? FONT.Brand : FONT.Excalifont) as AppState['currentItemFontFamily'],
      },
    }),
    [],
  );

  /** TYPE code of a placed fixture (measure labels); a new function only when the catalog changes. */
  const fixtureCode = useCallback(
    (f: FixtureData) => targetFor(estimateRef.current, targetOfFixture(f))?.code ?? null,
    [estimate.itemById, estimate.assemblyById], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // ---- measure panel cards (recomputed per scene tick) ----------------------------------------
  const panelImage = useMemo((): SelectedImage | null => {
    const id = focusImage;
    const el = id && xapi ? (xapi.getSceneElements().find((e) => e.id === id) as unknown as GeomEl | undefined) : undefined;
    if (!el || el.isDeleted || el.type !== 'image') return null;
    return { elementId: el.id, scaleLabel: scaleOf(el)?.label ?? null, locked: !!el.locked, hasSheetSize: !!sourceOf(el)?.page_pt, sourceLabel: sourceOf(el) ? imageName(el) : null };
  }, [focusImage, xapi, sceneKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const panelMeasure = useMemo((): SelectedMeasure | null => {
    const id = selection.line?.elementId ?? (selection.fixture && (selection.fixture.measure !== 'count' || selection.fixture.along) ? selection.fixture.elementId : null);
    if (!id || !xapi) return null;
    const scene = xapi.getSceneElements() as unknown as GeomEl[];
    const el = scene.find((e) => e.id === id);
    if (!el) return null;
    const f = fixtureOf(el as unknown as SceneEl);
    const kind: 'linear' | 'area' = f ? (f.measure === 'area' ? 'area' : 'linear') : measureOf(el)?.measure ?? (isAreaShape(el) ? 'area' : 'linear');
    const m = measureOnImage(scene, el, kind);
    const img = m ? scene.find((e) => e.id === m.image.id) : null;
    return {
      elementId: id,
      kind,
      text: m ? (kind === 'area' ? formatArea(m.value, m.unit, display) : formatLength(m.value, m.unit, display)) : null,
      on: img ? imageName(img) : null,
      spans: !!m?.spans,
      isMeasure: !!(f || measureOf(el)),
    };
  }, [selection, xapi, sceneKey, display]); // eslint-disable-line react-hooks/exhaustive-deps

  const toolbarLine = useMemo((): SelectedLineInfo | null => {
    const id = selection.line?.elementId ?? (selection.fixture && (selection.fixture.measure !== 'count' || selection.fixture.along) ? selection.fixture.elementId : null);
    if (!id || !xapi) return null;
    const scene = xapi.getSceneElements() as unknown as GeomEl[];
    const el = xapi.getSceneElements().find((e) => e.id === id) as (ExcalidrawElement & { elbowed?: boolean }) | undefined;
    if (!el) return null;
    const linear = isRunLike(el as never);
    const f = fixtureOf(el as unknown as SceneEl);
    const a = f?.measure === 'count' ? f.along_run : undefined;
    let along: SelectedLineInfo['along'] = null;
    if (a && f) {
      const m = measureOnImage(scene, el as unknown as GeomEl, 'linear');
      const len = m ? convertLength(m.value, m.unit, a.unit) : null;
      along = {
        itemId: f.item_id ?? '',
        code: targetFor(estimateRef.current, targetOfFixture(f))?.code ?? '',
        unitLength: a.unit_length,
        unit: a.unit,
        length: len,
        count: len == null ? null : countAlong(len, a.unit_length),
      };
    }
    return {
      along,
      elementId: id,
      type: linear ? (el.type as 'line' | 'arrow') : 'shape',
      curved: !!el.roundness,
      elbowed: !!el.elbowed,
      editing: xapi.getAppState().editingLinearElement?.elementId === id,
    };
  }, [selection, xapi, sceneKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const toolbarFixture = useMemo((): SelectedFixtureInfo | null => {
    const f = selection.fixture;
    if (!f || f.measure !== 'count' || f.along) return null;
    return { elementId: f.elementId, label: estimate.targetInfo(f.target)?.label ?? 'Fixture', qty: f.qty, measure: f.measure, scope: f.scope ?? '' };
  }, [selection.fixture, estimate.targetInfo]); // eslint-disable-line react-hooks/exhaustive-deps
  /** Drawn zones with their fixture counts (overlay labels, panel list, toolbar card). */
  const zoneList = useMemo(() => {
    if (!xapi || !ready) return [] as { id: string; name: string; count: number; scope: string; hidden: boolean; fixtures: string[]; pts: [number, number][] }[];
    const scene = xapi.getSceneElements() as unknown as GeomEl[];
    const zs = zonesOf(scene);
    const by = fixturesByZone(scene, zs);
    return zs.map((z) => ({ id: z.id, name: z.name, count: by.get(z.id)?.length ?? 0, scope: z.scope, hidden: z.hidden, fixtures: by.get(z.id) ?? [], pts: z.pts as [number, number][] }));
  }, [xapi, ready, sceneKey]); // eslint-disable-line react-hooks/exhaustive-deps
  /** The SCOPE dropdowns' list: the workbook's DASHBOARDS register (linked) + every scope the board uses. */
  const wbLink = useWorkbookLink(estimate.workbook, estimate.onFrame);
  const scopes = useMemo(() => {
    const placed = xapi && ready ? (xapi.getSceneElements() as readonly ExcalidrawElement[]).map((e) => fixtureOf(e)?.scope) : [];
    return estimateScopeChoices(estimate, wbLink, zoneList, placed);
  }, [estimate.items, estimate.assemblies, estimate.state, wbLink, zoneList]); // eslint-disable-line react-hooks/exhaustive-deps
  /** The selected fixture plus its zone's SCOPE (for the "Inherit (…)" label). */
  const panelFixture = useMemo((): SelectedFixture | null => {
    const f = selection.fixture;
    if (!f) return null;
    const z = zoneList.find((x) => x.fixtures.includes(f.elementId));
    return { ...f, zoneScope: z?.scope || undefined };
  }, [selection.fixture, zoneList]);
  const toolbarZone = useMemo(() => (selection.zone ? zoneList.find((z) => z.id === selection.zone) ?? null : null), [selection.zone, zoneList]);
  const toolbarDimension = useMemo(() => {
    const id = selection.line?.elementId;
    if (!id || !xapi) return null;
    const scene = xapi.getSceneElements() as unknown as GeomEl[];
    const el = scene.find((e) => e.id === id);
    const d = el && dimensionOf(el);
    if (!el || !d) return null;
    return { elementId: id, text: dimensionText(scene, el, display), ends: d.ends };
  }, [selection, xapi, sceneKey, display]); // eslint-disable-line react-hooks/exhaustive-deps
  const toolbarMode: MeasureMode = calibration ? 'calibrate' : zoneDraft ? 'zone' : mirror ? 'mirror' : dimension ? 'dimension' : measuring ? measuring.kind : armed ? 'fixture' : null;
  const openPanel = useCallback(() => {
    setEstimateOpen(true);
    writeFlag(estimateKey(boardId), '1');
  }, [boardId]);

  // ---- render ----------------------------------------------------------------------------
  if (loadError) {
    return (
      <div className="xb-center">
        <p className="xb-error">{loadError}</p>
        <button className="xb-btn" onClick={() => navigate('/')}>
          Back to your boards
        </button>
      </div>
    );
  }

  return (
    <div className="xb-board">
      <BoardHeader
        board={board}
        boards={boards}
        conn={conn}
        users={users}
        following={following}
        onFollow={follow}
        onRenamed={setBoard}
        xapi={xapi}
        estimateTotal={estimate.budget ? money(estimate.budget.totals.total, estimate.budget.currency) : null}
        estimateOpen={estimateOpen}
        onToggleEstimate={keyEstimate.togglePanel}
      />
      <div className="xb-canvas">
        <Excalidraw
          excalidrawAPI={setXapi}
          initialData={initialData}
          theme={theme}
          isCollaborating
          // Keys (undo, tools) keep working after using the estimate panel or header; Excalidraw still ignores inputs.
          handleKeyboardGlobally
          viewModeEnabled={readOnly}
          onChange={onChange}
          onPointerUpdate={onPointerUpdate}
          onPointerDown={onPointerDown as never}
          onUserFollow={(p) => {
            if (p.action === 'FOLLOW') {
              setFollowing(p.userToFollow.socketId);
              collabRef.current?.jumpTo(p.userToFollow.socketId);
            } else setFollowing(null);
          }}
          UIOptions={{ canvasActions: { toggleTheme: true, loadScene: false, saveToActiveFile: false } }}
          name={board?.title ?? 'board'}
        >
          <MainMenu>
            <MainMenu.Item onSelect={() => navigate('/')}>← All boards</MainMenu.Item>
            <MainMenu.Separator />
            <MainMenu.Item onSelect={() => setClean((c) => !c)}>{clean ? '' : '✓ '}Hand-drawn style (sketchy font and edges)</MainMenu.Item>
            <MainMenu.Item onSelect={zoomToFit}>Zoom to fit</MainMenu.Item>
            <MainMenu.DefaultItems.SaveAsImage />
            <MainMenu.DefaultItems.SearchMenu />
            <MainMenu.DefaultItems.Help />
            {!readOnly && <MainMenu.DefaultItems.ClearCanvas />}
            <MainMenu.Separator />
            <MainMenu.DefaultItems.ToggleTheme />
            <MainMenu.DefaultItems.ChangeCanvasBackground />
          </MainMenu>
          {!readOnly && (
            <Footer>
              <BoardTools
                activeTool={activeTool}
                stickyColor={stickyColor}
                onStickyColor={(c) => {
                  setStickyColor(c);
                  zoneInterrupt(() => armTool('sticky'));
                }}
                onArm={(tool) => zoneInterrupt(() => armTool(tool))}
                onPen={(kind) => zoneInterrupt(() => setPen(kind))}
                onLaser={() => zoneInterrupt(() => xapi?.setActiveTool({ type: 'laser' }))}
                onFrame={() => zoneInterrupt(() => xapi?.setActiveTool({ type: 'frame' }))}
                onFit={zoomToFit}
              />
            </Footer>
          )}
        </Excalidraw>
        {xapi && minimap ? <Minimap xapi={xapi} onClose={() => setMinimap(false)} /> : null}
        {xapi ? (
          <MiroKeysExcalidraw
            xapi={xapi}
            readOnly={!!readOnly}
            pointer={pointerRef}
            armTool={armTool}
            setPen={setPen}
            place={placeKind}
            onToggleMinimap={() => setMinimap((v) => !v)}
            onZoomToFit={zoomToFit}
            estimate={keyEstimate}
            intercept={zoneKey}
          />
        ) : null}
        {/* Screen-space overlays: the only part of the page that re-renders per camera frame. */}
        <CameraLayer clock={camera}>
          {(tick) => (
            <>
              {xapi && ready && zoneList.length ? <ZoneLabels xapi={xapi} tick={tick} zones={zoneList} /> : null}
              {xapi && ready && estimate.state ? <FixtureLabels xapi={xapi} tick={tick} sceneKey={sceneKey} estimate={estimate} /> : null}
              {xapi && ready && zoneDraft ? (
                <ZoneOverlay xapi={xapi} tick={tick} draft={zoneDraft} ghosts={zoneList.filter((z) => z.hidden).map((z) => z.pts)} onChange={setZone} onCommit={commitZone} onCancel={clearZone} />
              ) : null}
              {xapi && ready && mirror ? (
                <MirrorOverlay
                  xapi={xapi}
                  tick={tick}
                  mirror={mirror}
                  keep={mirrorKeep}
                  onKeep={(v) => {
                    setMirrorKeep(v);
                    writeMirrorKeep(v);
                  }}
                  onPoint={mirrorPoint}
                  onCancel={() => setMirror(null)}
                />
              ) : null}
              {xapi && ready ? (
                <MeasureOverlay
                  xapi={xapi}
                  tick={tick}
                  sceneKey={sceneKey}
                  display={display}
                  fixtureCode={fixtureCode}
                  draftId={draftId}
                  calibration={calibration}
                  readOnly={!!readOnly}
                  onBadge={(id) => {
                    setFocusImage(id);
                    setEstimateOpen(true);
                    writeFlag(estimateKey(boardId), '1');
                  }}
                  onUnlock={(id) => {
                    patchElement(id, { locked: false });
                    toast('Unlocked');
                  }}
                  onCalibrationPoint={calibrationPoint}
                  measuring={measuring ? { kind: measuring.kind, what: measuring.target ? estimate.targetInfo(measuring.target)?.label ?? null : null } : null}
                  placing={armed ? estimate.targetInfo(armed)?.label ?? 'a fixture' : null}
                  onCalibrationDone={(len) => void calibrationDone(len)}
                  onCalibrationCancel={() => setCalibration(null)}
                  dimension={dimension}
                  onDimensionPoint={dimensionPoint}
                  onDimensionCancel={() => setDimension(null)}
                />
              ) : null}
            </>
          )}
        </CameraLayer>
        {xapi && ready && !readOnly ? (
          <MeasureToolbar
            mode={toolbarMode}
            panelOpen={estimateOpen}
            display={display}
            unitsEnabled={!!estimate.state}
            image={panelImage}
            measure={panelMeasure}
            line={toolbarLine}
            fixture={toolbarFixture}
            dbl={dbl}
            onFixture={() => zoneInterrupt(() => (armed ? arm(null) : keyEstimate.arm()))}
            onRun={() => zoneInterrupt(() => (measuring?.kind === 'linear' ? xapi.setActiveTool({ type: 'selection' }) : keyEstimate.measure('linear')))}
            onArea={() => zoneInterrupt(() => (measuring?.kind === 'area' ? xapi.setActiveTool({ type: 'selection' }) : keyEstimate.measure('area')))}
            onCalibrate={() => zoneInterrupt(() => (calibration ? setCalibration(null) : startCalibrate(panelImage?.elementId ?? null)))}
            onPreset={(id, key) => void presetScale(id, key)}
            onToggleLock={toggleImageLock}
            onRemoveScale={(id) => setImageScale(id, null)}
            onUnits={() => void estimate.setPricing({ measure_display: nextDisplay(display) })}
            onTogglePanel={keyEstimate.togglePanel}
            onEditPoints={editPoints}
            onMeasureLine={measureLine}
            onAssign={openPanel}
            assignOptions={
              toolbarLine && !readOnly
                ? (() => {
                    const kind = assignKind(toolbarLine.elementId);
                    const uoms = assignUoms(kind);
                    const metric = (estimate.state?.pricing?.measure_display ?? 'ftin') === 'm';
                    return {
                      items: estimate.items.filter((i) => uoms.includes(i.uom)).map((i) => ({ id: i.id, label: `${i.code} ${i.description || i.name}` })),
                      // EACH types counted along a line (LED tubes, pixel bars…); not for closed shapes.
                      counts:
                        kind === 'linear'
                          ? estimate.items
                              .filter((i) => i.uom === 'EACH')
                              .map((i) => ({
                                id: i.id,
                                label: `${i.code} ${i.description || i.name}`,
                                unitLength: i.run_unit_length ?? null,
                                unit: (i.run_unit || (metric ? 'm' : 'ft')) as 'm' | 'ft',
                              }))
                          : [],
                      presets: FIXTURE_PRESETS.filter((x) => uoms.includes(x.uom)).map((x) => ({ key: x.key, label: `${x.name} (${presetUom(x.uom, estimate.state?.pricing?.measure_display)})` })),
                    };
                  })()
                : null
            }
            onAssignTo={(id, c) => void assignToItem(id, c)}
            onToggleCurve={toggleCurve}
            onDimension={() => zoneInterrupt(startDimension)}
            onMirror={readOnly ? undefined : () => zoneInterrupt(startMirror)}
            onZone={readOnly ? undefined : startZone}
            zone={toolbarZone}
            onRenameZone={renameZone}
            onFocusZone={focusZone}
            scopeChoices={scopes}
            onZoneScope={readOnly ? undefined : setEstimateScope}
            onZoneHidden={readOnly ? undefined : (id, h) => setZonesHidden([id], h)}
            onFixtureScope={readOnly ? undefined : setEstimateScope}
            dimension={toolbarDimension}
            onDimensionEnds={setDimensionEnds}
            onDelete={deleteElement}
            onDbl={setDblSettings}
            onResizeFixtures={readOnly ? undefined : resizeFixtures}
          />
        ) : null}
        {xapi && board && estimateOpen ? (
          <EstimatePanel
            e={estimate}
            title={board.title}
            readOnly={!!readOnly}
            armed={armed}
            onArm={(t) => zoneInterrupt(() => arm(t))}
            onPlaceAtCentre={(target, qty, length) =>
              zoneInterrupt(() => {
                const st = xapi.getAppState();
                const z = st.zoom.value;
                placeFixture(st.width / 2 / z - st.scrollX, st.height / 2 / z - st.scrollY, { target, qty, length });
              }, 'place')
            }
            selection={panelFixture}
            frame={selection.frame}
            selectedLinePx={selection.line}
            onSetFixture={setFixture}
            onReassign={reassignFixture}
            onAssignLine={assignLine}
            image={panelImage}
            measure={panelMeasure}
            onCalibrateImage={(id) => startCalibrate(id)}
            onPresetImage={(id, key) => void presetScale(id, key)}
            onToggleImageLock={toggleImageLock}
            onRemoveImageScale={(id) => setImageScale(id, null)}
            onMeasureLine={measureLine}
            onFocusTarget={focusTarget}
            zones={zoneList}
            onFocusZone={focusZone}
            onRenameZone={renameZone}
            scopeChoices={scopes}
            onZoneScope={readOnly ? undefined : setEstimateScope}
            onZonesHidden={readOnly ? undefined : setZonesHidden}
            onFixtureScope={readOnly ? undefined : setEstimateScope}
            onDrawZone={readOnly ? undefined : startZone}
            onClose={keyEstimate.togglePanel}
            quickAddRef={quickAddRef}
          />
        ) : null}
        {!ready && !loadError && conn !== 'no_access' && conn !== 'auth_failed' && <div className="xb-overlay xb-muted">Connecting to board…</div>}
        {ready && conn === 'reconnecting' && <div className="xb-banner">Reconnecting… your edits are kept and will sync when the connection is back.</div>}
        {conn === 'no_access' && (
          <div className="xb-overlay">
            <div className="xb-overlay-card">
              <p>You don’t have access to this board.</p>
              <button className="xb-btn" onClick={() => navigate('/')}>
                Back to your boards
              </button>
            </div>
          </div>
        )}
        {conn === 'auth_failed' && (
          <div className="xb-overlay">
            <div className="xb-overlay-card">
              <p>Your session has expired.</p>
              <button className="xb-btn xb-btn-primary" onClick={() => api.signOut()}>
                Sign in again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- footer tools -------------------------------------------------------------------------

function BoardTools(p: {
  activeTool: string;
  stickyColor: StickyColor;
  onStickyColor(c: StickyColor): void;
  onArm(t: CustomTool): void;
  onPen(k: 'pen' | 'highlighter'): void;
  onLaser(): void;
  onFrame(): void;
  onFit(): void;
}) {
  const [palette, setPalette] = useState(false);
  const is = (t: string) => (p.activeTool === t ? ' is-active' : '');
  return (
    <div className="xb-tools" role="toolbar" aria-label="Board tools">
      <div className="xb-tool-group">
        <button className={`xb-tool${is('sticky')}`} title={withKey('Sticky note (click on the board to place)', 'tool.sticky')} aria-label="Sticky note" data-tool="sticky" onClick={() => p.onArm('sticky')}>
          <span className="xb-sticky-swatch" style={{ background: STICKY_COLORS[p.stickyColor] }} />
        </button>
        <button className="xb-tool xb-tool-caret" aria-label="Sticky colour" title="Sticky colour" onClick={() => setPalette((v) => !v)}>
          ▾
        </button>
        {palette && (
          <div className="xb-palette" onMouseLeave={() => setPalette(false)}>
            {STICKY_ORDER.map((c) => (
              <button
                key={c}
                className={`xb-swatch${c === p.stickyColor ? ' is-active' : ''}`}
                style={{ background: STICKY_COLORS[c] }}
                title={c}
                aria-label={`${c} sticky`}
                data-sticky-color={c}
                onClick={() => {
                  p.onStickyColor(c);
                  setPalette(false);
                }}
              />
            ))}
          </div>
        )}
      </div>
      <button className={`xb-tool${is('triangle')}`} title="Triangle" aria-label="Triangle" data-tool="triangle" onClick={() => p.onArm('triangle')}>
        <svg viewBox="0 0 20 20" width="18" height="18">
          <path d="M10 3 L18 17 H2 Z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        </svg>
      </button>
      <button className={`xb-tool${is('star')}`} title="Star" aria-label="Star" data-tool="star" onClick={() => p.onArm('star')}>
        <svg viewBox="0 0 20 20" width="18" height="18">
          <path d="M10 2l2.4 5 5.4.6-4 3.7 1.1 5.4L10 14l-4.9 2.7 1.1-5.4-4-3.7 5.4-.6z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      </button>
      <span className="xb-tool-sep" />
      <button className={`xb-tool${is('pen')}`} title={withKey('Pen', 'tool.pen')} aria-label="Pen" data-tool="pen" onClick={() => p.onPen('pen')}>
        <svg viewBox="0 0 20 20" width="18" height="18">
          <path d="M3 17l1-4 9-9 3 3-9 9z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        </svg>
      </button>
      <button className={`xb-tool${is('highlighter')}`} title="Highlighter" aria-label="Highlighter" data-tool="highlighter" onClick={() => p.onPen('highlighter')}>
        <svg viewBox="0 0 20 20" width="18" height="18">
          <path d="M4 16h12" stroke="#fab005" strokeWidth="4" strokeLinecap="round" opacity=".6" />
          <path d="M6 12l6-8 3 2-6 8z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
      </button>
      <button className={`xb-tool${is('frame')}`} title={withKey('Frame (named section)', 'tool.frame')} aria-label="Frame" data-tool="frame" onClick={p.onFrame}>
        <svg viewBox="0 0 20 20" width="18" height="18">
          <path d="M5 2v16M15 2v16M2 5h16M2 15h16" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      </button>
      <button className={`xb-tool${is('laser')}`} title="Laser pointer" aria-label="Laser pointer" data-tool="laser" onClick={p.onLaser}>
        <svg viewBox="0 0 20 20" width="18" height="18">
          <circle cx="10" cy="10" r="3" fill="#e03131" />
          <circle cx="10" cy="10" r="7" fill="none" stroke="#e03131" strokeWidth="1" opacity=".5" />
        </svg>
      </button>
      <span className="xb-tool-sep" />
      <button className="xb-tool" title={withKey('Zoom to fit', 'nav.zoomFit')} aria-label="Zoom to fit" onClick={p.onFit}>
        <svg viewBox="0 0 20 20" width="18" height="18">
          <path d="M3 7V3h4M13 3h4v4M17 13v4h-4M7 17H3v-4" fill="none" stroke="currentColor" strokeWidth="1.6" />
        </svg>
      </button>
    </div>
  );
}

// ---- header --------------------------------------------------------------------------------

function BoardHeader(p: {
  board: Board | null;
  boards: BoardsApi;
  conn: ConnState;
  users: PresenceUser[];
  following: string | null;
  onFollow(u: PresenceUser | null): void;
  onRenamed(b: Board): void;
  xapi: ExcalidrawImperativeAPI | null;
  estimateTotal: string | null;
  estimateOpen: boolean;
  onToggleEstimate(): void;
}) {
  const { board } = p;
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const canEdit = board && board.role !== 'viewer';

  const saveTitle = async () => {
    setEditing(false);
    const t = title.trim();
    if (!board || !t || t === board.title) return;
    try {
      p.onRenamed(await p.boards.rename(board.id, t));
    } catch (e) {
      toast(`Rename failed: ${(e as Error).message}`, 'error');
    }
  };

  // Drive folder, who has access, optional view-only link (drive.tsx).
  const share = () => {
    if (board) openShare(board.id);
  };

  const others = p.users.filter((u) => !u.isSelf);
  const self = p.users.find((u) => u.isSelf);
  const status =
    p.conn === 'open' ? { text: 'Live', cls: 'ok' } : p.conn === 'connecting' ? { text: 'Connecting…', cls: 'warn' } : p.conn === 'reconnecting' ? { text: 'Reconnecting…', cls: 'warn' } : p.conn === 'no_access' ? { text: 'No access', cls: 'bad' } : p.conn === 'auth_failed' ? { text: 'Signed out', cls: 'bad' } : { text: 'Offline', cls: 'bad' };

  return (
    <header className="xb-board-head">
      <button className="xb-icon-btn" onClick={() => navigate('/')} title="All boards" aria-label="Back to all boards">
        ←
      </button>
      {editing ? (
        <input
          className="xb-title-input"
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={saveTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void saveTitle();
            if (e.key === 'Escape') setEditing(false);
          }}
          aria-label="Board title"
        />
      ) : (
        <button
          className="xb-title"
          disabled={!canEdit}
          title={canEdit ? 'Rename board' : undefined}
          onClick={() => {
            setTitle(board?.title ?? '');
            setEditing(true);
          }}
        >
          {board?.title ?? '…'}
        </button>
      )}
      {board?.role === 'viewer' && <span className="xb-chip">View only</span>}
      <span className={`xb-status xb-status-${status.cls}`} data-conn={p.conn}>
        <span className="xb-dot" />
        {status.text}
      </span>
      <div className="xb-grow" />
      <div className="xb-avatars" aria-label="People on this board">
        {others.map((u) => (
          <button
            key={u.socketId}
            className={`xb-avatar${p.following === u.socketId ? ' is-following' : ''}${u.state !== 'active' ? ' is-idle' : ''}`}
            style={{ background: excalidrawClientColor(String(u.userId)) }}
            title={`${u.userName}${u.state !== 'active' ? ` (${u.state})` : ''} — ${p.following === u.socketId ? 'stop following' : 'click to follow'}`}
            data-user={u.userName}
            onClick={() => p.onFollow(u)}
          >
            {initials(u.userName)}
          </button>
        ))}
        {self && (
          <span className="xb-avatar xb-avatar-self" style={{ background: excalidrawClientColor(String(self.userId)) }} title={`${self.userName} (you)`}>
            {initials(self.userName)}
          </span>
        )}
      </div>
      <button
        className={`xb-btn${p.estimateOpen ? ' is-active' : ''}`}
        onClick={p.onToggleEstimate}
        aria-pressed={p.estimateOpen}
        title={withKey('Design estimate: fixtures, BOM and ROM budget', 'estimate.panel')}
        data-action="estimate"
      >
        Estimate{p.estimateTotal ? <span className="xb-chip xb-chip-money">{p.estimateTotal}</span> : null}
      </button>
      {canEdit && (
        <button className="xb-btn xb-btn-primary" onClick={share} data-action="share">
          Share
        </button>
      )}
      <div className="xb-export">
        <button className="xb-btn" onClick={() => setExportOpen((v) => !v)} aria-expanded={exportOpen} data-action="export">
          Export ▾
        </button>
        {exportOpen && p.xapi && <ExportMenu xapi={p.xapi} title={board?.title ?? 'board'} onClose={() => setExportOpen(false)} />}
      </div>
    </header>
  );
}

function ExportMenu({ xapi, title, onClose }: { xapi: ExcalidrawImperativeAPI; title: string; onClose(): void }) {
  const els = xapi.getSceneElements();
  const sel = Object.keys(xapi.getAppState().selectedElementIds).length;
  const fr = frames(els as readonly ExcalidrawElement[]);
  const run = async (scope: ExportScope, format: ExportFormat, label: string) => {
    onClose();
    try {
      const blob = await exportScope(snapshot(xapi), scope, format);
      if (!blob) return toast('Nothing to export', 'error');
      download(blob, `${safeFilename(title)}${label ? `-${safeFilename(label)}` : ''}.${format}`);
    } catch (e) {
      toast(`Export failed: ${(e as Error).message}`, 'error');
    }
  };
  const row = (label: string, scope: ExportScope, disabled: boolean, suffix: string) => (
    <div className="xb-export-row" key={label}>
      <span>{label}</span>
      <button disabled={disabled} onClick={() => run(scope, 'png', suffix)}>
        PNG
      </button>
      <button disabled={disabled} onClick={() => run(scope, 'svg', suffix)}>
        SVG
      </button>
    </div>
  );
  return (
    <div className="xb-menu xb-export-menu" onMouseLeave={onClose}>
      {row('Whole board', { kind: 'board' }, els.length === 0, '')}
      {row(`Selection${sel ? ` (${sel})` : ''}`, { kind: 'selection' }, sel === 0, 'selection')}
      {fr.length > 0 && <div className="xb-menu-label">Frames</div>}
      {fr.map((f) => row(f.name || 'Frame', { kind: 'frame', frameId: f.id }, false, f.name || 'frame'))}
    </div>
  );
}
